import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCoachellaSchedule, scoreCoachellaLineup, getCoachellaActs, saveCustomSchedule, getCustomSchedule, createGroup, getGroups, joinGroup, leaveGroup, computeGroupSchedule, getGroupSchedule, saveGroupCustomSchedule, getTopArtists, getFestivals } from '@shared/services/api';
import { useLNAuth } from '@shared/context/LNAuthContext';
import ScheduleBuilder from '../components/ScheduleBuilder';
import MobileHeader from '../components/MobileHeader';
import { waitForImages } from '../logoData.js';
import { APP_URL } from '@shared/utils/urls';

const YEAR = 2026;

const FESTIVAL_DISPLAY = {
  coachella_weekend_1: 'Coachella Weekend 1',
  coachella_weekend_2: 'Coachella Weekend 2',
  stagecoach: 'Stagecoach',
  rolling_loud_orlando: 'Rolling Loud Orlando',
  bonnaroo: 'Bonnaroo',
};

// Per-festival share-card styling + disclaimers.
// headerLabel: shown top-left of every card (e.g. "Coachella 2026")
// disclaimer: legal small print at the bottom of every card
// gradients: { score, summary, fullSchedule } background gradients for the three
//   narrative slides that don't inherit day-specific colors
const FESTIVAL_CONFIG = {
  coachella_weekend_1: {
    headerLabel: `Coachella ${YEAR}`,
    shortName: 'Coachella',
    disclaimer: 'Not affiliated with Coachella, Goldenvoice, or AEG. Data by Spotify.',
    gradients: {
      score: 'linear-gradient(135deg, #1a2744 0%, #2d3a8e 15%, #7b2d8e 30%, #c24b8b 45%, #e85d75 60%, #e88d4f 80%, #1e3a5f 100%)',
      summary: 'linear-gradient(135deg, #1e3a5f 0%, #e88d4f 20%, #e85d75 40%, #c24b8b 55%, #7b2d8e 70%, #2d3a8e 85%, #1a2744 100%)',
      fullSchedule: 'linear-gradient(135deg, #1e3a5f 0%, #e88d4f 20%, #e85d75 40%, #c24b8b 55%, #7b2d8e 70%, #2d3a8e 85%, #1a2744 100%)',
      narrativeThumb: 'linear-gradient(135deg, #f97316 0%, #ec4899 50%, #9333ea 100%)',
    },
    dayGradients: {
      friday: 'from-orange-600 via-pink-600 to-purple-700',
      saturday: 'from-blue-600 via-cyan-500 to-teal-500',
      sunday: 'from-violet-600 via-purple-500 to-fuchsia-500',
    },
    dayBarColors: {
      friday: 'bg-gradient-to-r from-orange-500 to-pink-500',
      saturday: 'bg-gradient-to-r from-blue-500 to-cyan-400',
      sunday: 'bg-gradient-to-r from-violet-500 to-fuchsia-400',
    },
    dayBorderColors: {
      friday: 'border-orange-500/30',
      saturday: 'border-blue-500/30',
      sunday: 'border-violet-500/30',
    },
    dayTextColors: {
      friday: 'text-orange-400',
      saturday: 'text-cyan-400',
      sunday: 'text-violet-400',
    },
    // Animated background gradients for the scoring animation overlay.
    // `scanning` and `integrating` run during phases 0-1 (before day reveal).
    // Day entries run during phases 2-4 (one per day reveal).
    animationBackgrounds: {
      scanning: 'linear-gradient(135deg, #1a1a2e 0%, #2d3a8e 40%, #7b2d8e 80%, #1a1a2e 100%)',
      integrating: 'linear-gradient(135deg, #1a1a2e 0%, #7b2d8e 40%, #c24b8b 70%, #1a1a2e 100%)',
      friday: 'linear-gradient(135deg, #1a1a2e 0%, #c2543d 30%, #e85d75 60%, #7b2d8e 100%)',
      saturday: 'linear-gradient(135deg, #1a1a2e 0%, #1e5a8a 30%, #2dd4bf 60%, #1a1a2e 100%)',
      sunday: 'linear-gradient(135deg, #1a1a2e 0%, #7c3aed 30%, #c026d3 60%, #1a1a2e 100%)',
    },
  },
  coachella_weekend_2: {
    headerLabel: `Coachella ${YEAR}`,
    shortName: 'Coachella',
    disclaimer: 'Not affiliated with Coachella, Goldenvoice, or AEG. Data by Spotify.',
    gradients: {
      score: 'linear-gradient(135deg, #1a2744 0%, #2d3a8e 15%, #7b2d8e 30%, #c24b8b 45%, #e85d75 60%, #e88d4f 80%, #1e3a5f 100%)',
      summary: 'linear-gradient(135deg, #1e3a5f 0%, #e88d4f 20%, #e85d75 40%, #c24b8b 55%, #7b2d8e 70%, #2d3a8e 85%, #1a2744 100%)',
      fullSchedule: 'linear-gradient(135deg, #1e3a5f 0%, #e88d4f 20%, #e85d75 40%, #c24b8b 55%, #7b2d8e 70%, #2d3a8e 85%, #1a2744 100%)',
      narrativeThumb: 'linear-gradient(135deg, #f97316 0%, #ec4899 50%, #9333ea 100%)',
    },
    dayGradients: {
      friday: 'from-orange-600 via-pink-600 to-purple-700',
      saturday: 'from-blue-600 via-cyan-500 to-teal-500',
      sunday: 'from-violet-600 via-purple-500 to-fuchsia-500',
    },
    dayBarColors: {
      friday: 'bg-gradient-to-r from-orange-500 to-pink-500',
      saturday: 'bg-gradient-to-r from-blue-500 to-cyan-400',
      sunday: 'bg-gradient-to-r from-violet-500 to-fuchsia-400',
    },
    dayBorderColors: {
      friday: 'border-orange-500/30',
      saturday: 'border-blue-500/30',
      sunday: 'border-violet-500/30',
    },
    dayTextColors: {
      friday: 'text-orange-400',
      saturday: 'text-cyan-400',
      sunday: 'text-violet-400',
    },
    animationBackgrounds: {
      scanning: 'linear-gradient(135deg, #1a1a2e 0%, #2d3a8e 40%, #7b2d8e 80%, #1a1a2e 100%)',
      integrating: 'linear-gradient(135deg, #1a1a2e 0%, #7b2d8e 40%, #c24b8b 70%, #1a1a2e 100%)',
      friday: 'linear-gradient(135deg, #1a1a2e 0%, #c2543d 30%, #e85d75 60%, #7b2d8e 100%)',
      saturday: 'linear-gradient(135deg, #1a1a2e 0%, #1e5a8a 30%, #2dd4bf 60%, #1a1a2e 100%)',
      sunday: 'linear-gradient(135deg, #1a1a2e 0%, #7c3aed 30%, #c026d3 60%, #1a1a2e 100%)',
    },
  },
  stagecoach: {
    headerLabel: `Stagecoach ${YEAR}`,
    shortName: 'Stagecoach',
    disclaimer: 'Not affiliated with Stagecoach, Goldenvoice, or AEG. Data by Spotify.',
    gradients: {
      score: 'linear-gradient(135deg, #1a0e08 0%, #2d1810 15%, #6b2e1f 35%, #a13a22 55%, #c44d2e 70%, #e8954a 85%, #f5c373 100%)',
      summary: 'linear-gradient(135deg, #f5c373 0%, #e8954a 20%, #c44d2e 40%, #a13a22 55%, #6b2e1f 70%, #2d1810 85%, #1a0e08 100%)',
      fullSchedule: 'linear-gradient(135deg, #f5c373 0%, #e8954a 20%, #c44d2e 40%, #a13a22 55%, #6b2e1f 70%, #2d1810 85%, #1a0e08 100%)',
      narrativeThumb: 'linear-gradient(135deg, #2d1810 0%, #c44d2e 50%, #f5c373 100%)',
    },
    // Same warm desert palette across days — evening deepens Fri→Sat→Sun
    dayGradients: {
      friday: 'from-amber-500 via-orange-600 to-red-800',
      saturday: 'from-orange-500 via-red-700 to-amber-900',
      sunday: 'from-yellow-600 via-orange-700 to-stone-900',
    },
    dayBarColors: {
      friday: 'bg-gradient-to-r from-amber-400 to-orange-500',
      saturday: 'bg-gradient-to-r from-orange-400 to-red-600',
      sunday: 'bg-gradient-to-r from-yellow-500 to-orange-600',
    },
    dayBorderColors: {
      friday: 'border-amber-500/30',
      saturday: 'border-orange-500/30',
      sunday: 'border-yellow-600/30',
    },
    dayTextColors: {
      friday: 'text-amber-300',
      saturday: 'text-orange-300',
      sunday: 'text-yellow-400',
    },
    animationBackgrounds: {
      scanning: 'linear-gradient(135deg, #1a0e08 0%, #2d1810 40%, #6b2e1f 80%, #1a0e08 100%)',
      integrating: 'linear-gradient(135deg, #1a0e08 0%, #6b2e1f 40%, #c44d2e 70%, #1a0e08 100%)',
      friday: 'linear-gradient(135deg, #1a0e08 0%, #b45309 30%, #ea580c 60%, #991b1b 100%)',
      saturday: 'linear-gradient(135deg, #1a0e08 0%, #ea580c 30%, #b91c1c 60%, #1c0a05 100%)',
      sunday: 'linear-gradient(135deg, #1a0e08 0%, #ca8a04 30%, #c2410c 60%, #1c1917 100%)',
    },
  },
  rolling_loud_orlando: {
    headerLabel: `Rolling Loud ${YEAR}`,
    shortName: 'Rolling Loud',
    disclaimer: 'Not affiliated with Rolling Loud. Data by Spotify.',
    // Dark gunmetal/rock-metallic core with ~80% of every gradient anchored in
    // black/charcoal/steel. Each variant is the same metal palette, just shifted
    // slightly in undertone to feel distinct: warm-iron / cold-steel / charcoal-bronze.
    // Accent colors (ember, magenta, cyan) appear only as ~10-15% slivers near the end.
    //   inky black #050507, void #0d0d11, gunmetal-warm #1c1814, gunmetal-cool #14181c,
    //   charcoal #232026, steel #2e2e34, iron-warm #3a2e26, iron-cool #262e3a,
    //   ember accent #b91c1c, magenta accent #e91e63, cyan accent #2dd4bf
    gradients: {
      // Score card: metal core, ember warmth bleeds through the back half before resolving
      score: 'linear-gradient(135deg, #050507 0%, #0d0d11 14%, #14181c 26%, #1f1a1c 38%, #2a1f1f 52%, #3a2424 64%, #5a1c1c 78%, #832020 90%, #b91c1c 100%)',
      summary: 'linear-gradient(135deg, #b91c1c 0%, #832020 10%, #5a1c1c 22%, #3a2424 36%, #2a1f1f 48%, #1f1a1c 62%, #14181c 76%, #0d0d11 88%, #050507 100%)',
      fullSchedule: 'linear-gradient(135deg, #b91c1c 0%, #832020 10%, #5a1c1c 22%, #3a2424 36%, #2a1f1f 48%, #1f1a1c 62%, #14181c 76%, #0d0d11 88%, #050507 100%)',
      narrativeThumb: 'linear-gradient(135deg, #050507 0%, #2a1f1f 45%, #5a1c1c 70%, #b91c1c 100%)',
    },
    // Per-day full-bleed gradients, modeled after gradients.summary/fullSchedule:
    // dark gunmetal core that bleeds slowly into the day's accent.
    dayGradients: {
      friday: 'linear-gradient(135deg, #050507 0%, #0d0d11 14%, #14181c 26%, #1f1a1c 38%, #2a1f1f 52%, #3a2422 64%, #5a2a22 78%, #832525 90%, #b91c1c 100%)',
      saturday: 'linear-gradient(135deg, #050507 0%, #0d0d11 14%, #14181c 26%, #1f1a1f 38%, #2a1f26 52%, #3a242e 64%, #5a2a3c 78%, #832548 90%, #be185d 100%)',
      sunday: 'linear-gradient(135deg, #050507 0%, #0d0d11 14%, #14181c 26%, #161c1f 38%, #1d2326 52%, #1f2c30 64%, #1f3a3a 78%, #1d4d4a 90%, #0d9488 100%)',
    },
    dayBarColors: {
      friday: 'bg-gradient-to-r from-red-600 to-orange-500',
      saturday: 'bg-gradient-to-r from-pink-600 to-rose-400',
      sunday: 'bg-gradient-to-r from-teal-500 to-cyan-400',
    },
    dayBorderColors: {
      friday: 'border-stone-700/40',
      saturday: 'border-slate-700/40',
      sunday: 'border-zinc-700/40',
    },
    dayTextColors: {
      friday: 'text-stone-300',
      saturday: 'text-slate-300',
      sunday: 'text-zinc-300',
    },
    animationBackgrounds: {
      scanning: 'linear-gradient(135deg, #050507 0%, #0d0d11 25%, #14181c 50%, #1f1f24 75%, #050507 100%)',
      // Ember bleeds gradually but caps at a deep muted ember — never full saturation
      integrating: 'linear-gradient(135deg, #050507 0%, #0d0d11 22%, #14181c 38%, #1c1816 52%, #241c1a 64%, #2c1f1c 76%, #3a2422 88%, #4a261f 100%)',
      // Friday: warm iron with a deep ember glow — never reaches full saturation
      friday: 'linear-gradient(135deg, #050507 0%, #0d0d11 22%, #14181c 38%, #1c1816 52%, #241c1a 64%, #2c1f1c 76%, #3a2422 88%, #4a261f 100%)',
      // Saturday: cold steel with a faint magenta-bruised undertone in the deep shadows
      saturday: 'linear-gradient(135deg, #050507 0%, #0d0d11 22%, #14181c 38%, #181820 52%, #1d1923 64%, #251a26 76%, #311a2c 88%, #3d1c33 100%)',
      // Sunday: charcoal-bronze with a deep teal shadow glow at the tail
      sunday: 'linear-gradient(135deg, #050507 0%, #0d0d11 22%, #14181c 38%, #161c1f 52%, #1a2226 64%, #1d2c30 76%, #1f3a3a 88%, #234c44 100%)',
    },
  },
  bonnaroo: {
    headerLabel: `Bonnaroo ${YEAR}`,
    shortName: 'Bonnaroo',
    disclaimer: 'Not affiliated with Bonnaroo or AC Entertainment. Data by Spotify.',
    // Pulled directly from the official 2026 schedule cards:
    //   Friday banner ≈ rust/burnt-sienna (#5a2418 → #b8421c)
    //   Saturday banner ≈ deep wine/maroon (#3d0e1a → #8b1538)
    //   Sunday banner ≈ deep teal/forest (#0a3a35 → #2dd4bf)
    //   Logo wordmark = bright lime green (#84cc16)
    //   Stage tags: WHAT lime, WHICH orange, THIS pink, THAT lavender, OTHER aqua
    gradients: {
      // Score card: dark earth core sweeping through all three day colors,
      // landing on lime green (the wordmark color) as the finale
      score: 'linear-gradient(135deg, #1a0a0a 0%, #3d0e1a 16%, #5a2418 32%, #8b1538 46%, #b8421c 58%, #0a3a35 74%, #15803d 88%, #84cc16 100%)',
      summary: 'linear-gradient(135deg, #84cc16 0%, #15803d 14%, #0a3a35 28%, #b8421c 42%, #8b1538 56%, #5a2418 70%, #3d0e1a 85%, #1a0a0a 100%)',
      fullSchedule: 'linear-gradient(135deg, #84cc16 0%, #15803d 14%, #0a3a35 28%, #b8421c 42%, #8b1538 56%, #5a2418 70%, #3d0e1a 85%, #1a0a0a 100%)',
      narrativeThumb: 'linear-gradient(135deg, #5a2418 0%, #8b1538 50%, #0a3a35 100%)',
    },
    // Per-day backgrounds modeled on the actual schedule-image banners.
    // Each starts dark (top-left) and warms/cools into the day's banner color.
    dayGradients: {
      friday: 'linear-gradient(135deg, #1a0a06 0%, #2a1208 18%, #3d1810 32%, #5a2418 48%, #7a2c1a 62%, #9c3818 78%, #b8421c 90%, #d9621e 100%)',
      saturday: 'linear-gradient(135deg, #0f0508 0%, #1f0810 18%, #2e0c16 32%, #3d0e1a 48%, #5a1424 62%, #7a1530 78%, #8b1538 90%, #a8244f 100%)',
      sunday: 'linear-gradient(135deg, #050f0e 0%, #0a1f1c 18%, #0a2e2a 32%, #0a3a35 48%, #115a4f 62%, #15806a 78%, #1ca088 90%, #2dd4bf 100%)',
    },
    // Accent bars echo each day's banner color (rust / wine / teal)
    dayBarColors: {
      friday: 'bg-gradient-to-r from-orange-700 to-amber-500',
      saturday: 'bg-gradient-to-r from-rose-800 to-pink-500',
      sunday: 'bg-gradient-to-r from-teal-700 to-cyan-400',
    },
    dayBorderColors: {
      friday: 'border-orange-700/40',
      saturday: 'border-rose-800/40',
      sunday: 'border-teal-700/40',
    },
    dayTextColors: {
      friday: 'text-orange-400',
      saturday: 'text-rose-400',
      sunday: 'text-teal-400',
    },
    animationBackgrounds: {
      // Scanning/integrating: dark earth → lime (logo color), like dawn at the farm
      scanning: 'linear-gradient(135deg, #1a0a0a 0%, #15803d 40%, #84cc16 80%, #1a0a0a 100%)',
      integrating: 'linear-gradient(135deg, #1a0a0a 0%, #15803d 35%, #65a30d 65%, #84cc16 100%)',
      friday: 'linear-gradient(135deg, #1a0a06 0%, #5a2418 30%, #b8421c 60%, #d9621e 100%)',
      saturday: 'linear-gradient(135deg, #0f0508 0%, #3d0e1a 30%, #8b1538 60%, #a8244f 100%)',
      sunday: 'linear-gradient(135deg, #050f0e 0%, #0a3a35 30%, #15806a 60%, #2dd4bf 100%)',
    },
  },
};

const DEFAULT_FESTIVAL_CONFIG = FESTIVAL_CONFIG.coachella_weekend_1;
const getFestivalConfig = (name) => FESTIVAL_CONFIG[name] || DEFAULT_FESTIVAL_CONFIG;

// dayGradients can be either a tailwind class string (e.g. "from-orange-600 via-pink-600 to-purple-700")
// or a full CSS linear-gradient string. Returns ({ className, style }) for either form.
const dayGradientProps = (value) => {
  if (typeof value === 'string' && value.startsWith('linear-gradient')) {
    return { className: '', style: { background: value } };
  }
  return { className: `bg-gradient-to-br ${value}`, style: undefined };
};

function ScoreBadge({ score }) {
  if (!score) return null;
  let color = 'bg-white/20';
  if (score >= 70) color = 'bg-green-500';
  else if (score >= 40) color = 'bg-amber-500';
  else if (score >= 20) color = 'bg-orange-500';
  return <span className={`${color} text-white text-[10px] font-black px-1.5 py-0.5 rounded`}>{score}</span>;
}

// ── Animated counter ────────────────────────────────────────────────────────

function AnimatedScore({ target, duration = 1500 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease out cubic
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration]);
  return <>{count}</>;
}

// ── Slide components ────────────────────────────────────────────────────────

function ScoreRing({ score, size = 160, strokeWidth = 6, className = '' }) {
  const ringColor = score >= 70 ? '#22c55e' : score >= 40 ? '#eab308' : '#ef4444';
  const r = (size / 2) - (strokeWidth + 2);
  const circumference = 2 * Math.PI * r;
  return (
    <svg className={`rotate-90 scale-x-[-1] ${className}`} width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={strokeWidth} />
      <circle
        cx={size/2} cy={size/2} r={r} fill="none" stroke={ringColor} strokeWidth={strokeWidth} strokeLinecap="round"
        strokeDasharray={`${(score / 100) * circumference} ${circumference}`}
        className="animate-[drawRing_1.5s_ease-out_0.6s_both]"
      />
    </svg>
  );
}

function ScoreSlide({ score, festivalConfig = DEFAULT_FESTIVAL_CONFIG }) {
  return (
    <div className="flex items-center justify-center h-full p-6 md:p-8 animate-[fadeIn_0.5s_ease-out] relative overflow-hidden" style={{
      background: festivalConfig.gradients.score,
    }}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative z-10 flex flex-col items-center text-center">
        {/* Header */}
        <div className="flex items-center justify-between w-full mb-6">
          <p className="text-white/40 text-xs uppercase tracking-[0.3em]">{festivalConfig.headerLabel}</p>
          <img src="/logo-icon.png" alt="LN" className="h-8 opacity-70" />
        </div>

        {/* Score */}
        <p className="text-white/40 text-xs uppercase tracking-[0.3em] mb-6 animate-[fadeIn_0.5s_ease-out_0.2s_both]">Your Match</p>
        <div className="relative w-40 h-40 mb-6 animate-[scaleIn_0.8s_ease-out_0.4s_both]">
          <ScoreRing score={score} size={160} strokeWidth={6} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-6xl font-black text-white"><AnimatedScore target={score} duration={1500} /></span>
          </div>
        </div>
        <p className="text-white/30 text-xs animate-[fadeIn_0.5s_ease-out_1.5s_both]">Based on your Spotify listening</p>
        <p className="text-white/20 text-xs mt-6 animate-[fadeIn_0.5s_ease-out_2s_both]">Tap to see your lineup for each day &rarr;</p>

        {/* Footer */}
        <div className="flex items-center justify-between w-full mt-8">
          <p className="text-white/40 text-[8px] uppercase tracking-[0.25em]">loudneighbors.com</p>
          <img src="/logo-text.png" alt="Loud Neighbors" className="h-10 opacity-80" />
        </div>
        <p className="text-white/20 text-[6px] text-right mt-1">{festivalConfig.disclaimer}</p>
      </div>
    </div>
  );
}

function DaySlide({ day, artists, dayLabel, dayScore, festivalConfig = DEFAULT_FESTIVAL_CONFIG }) {
  const { className: dayBg, style: dayBgStyle } = dayGradientProps(festivalConfig.dayGradients[day]);
  return (
    <div className={`flex items-center justify-center h-full ${dayBg} p-6 md:p-8`} style={dayBgStyle}>
      <div className="flex flex-col w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 animate-[fadeIn_0.3s_ease-out]">
          <p className="text-white/40 text-xs uppercase tracking-[0.3em]">{festivalConfig.headerLabel}</p>
          <img src="/logo-icon.png" alt="LN" className="h-8 opacity-70" />
        </div>

        {/* Day + score */}
        <div className="flex items-center gap-4 mb-6">
          <h2 className="text-white font-black text-4xl md:text-5xl uppercase animate-[fadeIn_0.3s_ease-out_0.1s_both]">{dayLabel}</h2>
          {dayScore > 0 && (
            <div className="relative w-14 h-14 animate-[scaleIn_0.5s_ease-out_0.2s_both]">
              <ScoreRing score={dayScore} size={56} strokeWidth={4} />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-black text-sm">{dayScore}</span>
              </div>
            </div>
          )}
        </div>

        {/* Artists */}
        <div className="space-y-4">
          {artists.map((a, i) => (
            <div
              key={a.artist_spotify_id || i}
              className="flex items-center gap-4 animate-[slideIn_0.4s_ease-out_both]"
              style={{ animationDelay: `${0.2 + i * 0.15}s` }}
            >
              <span className="text-white/40 text-2xl font-black w-8 text-right">{i + 1}</span>
              {a.artist_image_url ? (
                <img src={a.artist_image_url} alt="" className="w-14 h-14 rounded-full border-2 border-white/20 object-cover" loading="lazy" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                  {(a.artist_name || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-lg leading-tight">{a.artist_name}</p>
              </div>
              <ScoreBadge score={a.score} />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-6">
          <p className="text-white/40 text-[8px] uppercase tracking-[0.25em]">loudneighbors.com</p>
          <img src="/logo-text.png" alt="Loud Neighbors" className="h-10 opacity-60" />
        </div>
        <p className="text-white/20 text-[6px] text-right mt-1">{festivalConfig.disclaimer}</p>
      </div>
    </div>
  );
}

function formatTime(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

function ScheduleSlide({ day, dayLabel, acts, allDayActs, dayScore, festivalConfig = DEFAULT_FESTIVAL_CONFIG }) {
  // Use optimal_schedule acts (have set times), take top 8 by score, sort by time desc
  const scheduleActs = [...(acts || [])]
    .filter(a => a.set_start)
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .slice(0, 8)
    .sort((a, b) => new Date(b.set_start) - new Date(a.set_start));

  const { className: dayBg, style: dayBgStyle } = dayGradientProps(festivalConfig.dayGradients[day]);
  return (
    <div className={`flex items-center justify-center h-full ${dayBg} p-5 md:p-8`} style={dayBgStyle}>
      <div className="flex flex-col w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-2 animate-[fadeIn_0.3s_ease-out]">
          <p className="text-white/40 text-xs uppercase tracking-[0.3em]">{festivalConfig.headerLabel}</p>
          <img src="/logo-icon.png" alt="LN" className="h-8 opacity-70" />
        </div>

        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-white font-black text-xl md:text-2xl uppercase animate-[fadeIn_0.3s_ease-out_0.1s_both]">
            Your {dayLabel} Schedule
          </h2>
          {dayScore > 0 && (
            <div className="relative w-10 h-10 animate-[scaleIn_0.5s_ease-out_0.2s_both]">
              <ScoreRing score={dayScore} size={40} strokeWidth={3} />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-black text-[10px]">{dayScore}</span>
              </div>
            </div>
          )}
        </div>

        {/* Top 8 by score, ordered by time desc */}
        <div className="space-y-1.5 overflow-y-auto max-h-[65vh]">
          {scheduleActs.map((a, i) => (
            <div
              key={a.artist_spotify_id || a.act_id || i}
              className="flex items-center gap-3 bg-black/20 rounded-xl px-3 py-2 animate-[slideIn_0.4s_ease-out_both]"
              style={{ animationDelay: `${0.1 + i * 0.08}s` }}
            >
              {a.artist_image_url ? (
                <img src={a.artist_image_url} alt="" className="w-9 h-9 rounded-full border border-white/20 object-cover flex-shrink-0" loading="lazy" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white/30 font-bold text-xs flex-shrink-0">
                  {(a.artist_name || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm leading-tight truncate">{a.artist_name}</p>
                <p className="text-white/40 text-[11px]">
                  {a.stage} &middot; {formatTime(a.set_start)}{a.set_end ? ` – ${formatTime(a.set_end)}` : ''}
                </p>
              </div>
              <ScoreBadge score={a.score} />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-4">
          <p className="text-white/40 text-[8px] uppercase tracking-[0.25em]">loudneighbors.com</p>
          <img src="/logo-text.png" alt="Loud Neighbors" className="h-10 opacity-60" />
        </div>
        <p className="text-white/20 text-[6px] text-right mt-1">{festivalConfig.disclaimer}</p>
      </div>
    </div>
  );
}

function SummarySlide({ schedule, festivalConfig = DEFAULT_FESTIVAL_CONFIG }) {
  const days = ['friday', 'saturday', 'sunday'];
  const dayLabels = { friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
  const barColors = festivalConfig.dayBarColors;
  return (
    <div data-share-card className="flex items-center justify-center h-full p-6 md:p-8 animate-[fadeIn_0.5s_ease-out] relative overflow-hidden" style={{
      background: festivalConfig.gradients.summary,
    }}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative z-10 flex flex-col w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-white/40 text-xs uppercase tracking-[0.3em]">{festivalConfig.headerLabel}</p>
          <img src="/logo-icon.png" alt="LN" className="h-8 opacity-70" />
        </div>

        {/* Score + title */}
        <div className="flex items-center gap-4 mb-6">
          <h2 className="text-white font-black text-2xl uppercase">Your Lineup</h2>
          <div className="relative w-12 h-12">
            <ScoreRing score={schedule.composite_score} size={48} strokeWidth={3} />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-black text-sm">{schedule.composite_score}</span>
            </div>
          </div>
        </div>

        {/* Days */}
        <div className="space-y-5">
          {days.map((day, dayIdx) => {
            const top5 = schedule.days?.[day]?.top5 || [];
            const dayScore = Math.round(schedule.day_scores?.[day] || 0);
            return (
              <div key={day} className="animate-[fadeIn_0.4s_ease-out_both]" style={{ animationDelay: `${dayIdx * 0.2}s` }}>
                <div className="flex items-center gap-3 mb-2">
                  <p className="text-white font-black text-sm uppercase w-20 flex-shrink-0">{dayLabels[day]}</p>
                  <div className="flex-1 h-3 bg-white/10 overflow-hidden">
                    <div className={`h-full ${barColors[day]} transition-all duration-1000`} style={{ width: `${dayScore}%` }} />
                  </div>
                  <span className="text-white font-black text-sm w-8 text-right">{dayScore}</span>
                </div>
                <div className="grid grid-cols-5 gap-3">
                  {top5.map((a, i) => (
                    <div key={a.artist_spotify_id || i} className="flex flex-col items-center">
                      <div className="relative w-12 h-12 md:w-14 md:h-14">
                        {a.artist_image_url ? (
                          <img src={a.artist_image_url} alt="" className="w-full h-full rounded-full object-cover bg-white/5" loading="lazy" />
                        ) : (
                          <div className="w-full h-full rounded-full bg-white/5 flex items-center justify-center text-white/20 text-xs font-bold">
                            {(a.artist_name || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2"><ScoreBadge score={a.score} /></div>
                      </div>
                      <p className="text-white text-[8px] md:text-[10px] mt-1.5 text-center leading-tight">{a.artist_name}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-6">
          <p className="text-white/40 text-[8px] uppercase tracking-[0.25em]">loudneighbors.com</p>
          <img src="/logo-text.png" alt="Loud Neighbors" className="h-10 opacity-80" />
        </div>
        <p className="text-white/20 text-[6px] text-right mt-1">{festivalConfig.disclaimer}</p>
      </div>
    </div>
  );
}

function FullScheduleSlide({ schedule, festivalConfig = DEFAULT_FESTIVAL_CONFIG }) {
  const days = ['friday', 'saturday', 'sunday'];
  const dayLabels = { friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
  const dayColors = festivalConfig.dayBorderColors;
  const textColors = festivalConfig.dayTextColors;
  return (
    <div className="flex items-center justify-center h-full p-4 md:p-6 overflow-y-auto relative" style={{
      background: festivalConfig.gradients.fullSchedule,
    }}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative z-10 flex flex-col w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-2 animate-[fadeIn_0.3s_ease-out]">
          <p className="text-white/40 text-xs uppercase tracking-[0.3em]">{festivalConfig.headerLabel}</p>
          <img src="/logo-icon.png" alt="LN" className="h-8 opacity-70" />
        </div>
        <div className="flex items-center gap-3 mb-3 animate-[fadeIn_0.3s_ease-out_0.1s_both]">
          <h2 className="text-white font-black text-xl uppercase whitespace-nowrap">Your Full Schedule</h2>
          <div className="relative w-10 h-10">
            <ScoreRing score={schedule.composite_score} size={40} strokeWidth={3} />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-black text-[10px]">{schedule.composite_score}</span>
            </div>
          </div>
        </div>

        {/* 3-column grid */}
        <div className="grid grid-cols-3 gap-2">
          {days.map((day, dayIdx) => {
            const acts = schedule.optimal_schedule?.[day] || [];
            if (!acts.length) return null;
            return (
              <div key={day} className="animate-[fadeIn_0.4s_ease-out_both]" style={{ animationDelay: `${dayIdx * 0.15}s` }}>
                <div className="flex items-center justify-center gap-2 mb-1.5">
                  <p className={`${textColors[day]} font-black text-[10px] uppercase tracking-wider`}>{dayLabels[day]}</p>
                  {schedule.day_scores?.[day] > 0 && (
                    <div className="relative w-6 h-6">
                      <ScoreRing score={Math.round(schedule.day_scores[day])} size={24} strokeWidth={2} />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-white font-black text-[7px]">{Math.round(schedule.day_scores[day])}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  {[...acts].reverse().slice(0, 8).map((a, i) => (
                    <div key={a.act_id || i} className={`bg-black/20 border ${dayColors[day]} rounded-lg px-1.5 py-1.5 overflow-hidden`}>
                      <div className="flex items-center gap-1.5">
                        {a.artist_image_url ? (
                          <img src={a.artist_image_url} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" loading="lazy" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-[8px] flex-shrink-0">
                            {(a.artist_name || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white/30 text-[6px] leading-tight truncate">{a.stage}</p>
                          <p className="text-white font-bold text-[7px] leading-tight truncate">{a.artist_name}</p>
                          <p className="text-white/40 text-[6px]">{formatTime(a.set_start).replace(' ', '')}{a.set_end ? `–${formatTime(a.set_end).replace(' ', '')}` : ''}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-3">
          <p className="text-white/40 text-[8px] uppercase tracking-[0.25em]">loudneighbors.com</p>
          <img src="/logo-text.png" alt="Loud Neighbors" className="h-8 opacity-80" />
        </div>
        <p className="text-white/20 text-[6px] text-right mt-1">{festivalConfig.disclaimer}</p>
      </div>
    </div>
  );
}

// ── Share Modal with card selector ──────────────────────────────────────────

function ShareModal({ schedule, initialCardType, onClose, festival }) {
  const festivalConfig = getFestivalConfig(festival);
  const [selectedCard, setSelectedCard] = useState(-1);
  const [exporting, setExporting] = useState(false);
  const [imagesReady, setImagesReady] = useState(false);
  const cardRefs = useRef([]);
  const selectorRef = useRef(null);

  const days = ['friday', 'saturday', 'sunday'];
  const dayLabels = { friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
  const gradients = festivalConfig.dayGradients;

  const hasSchedule = schedule.optimal_schedule && Object.values(schedule.optimal_schedule).some(d => d?.length > 0);
  const cards = [
    { type: 'score', label: 'Score' },
    ...days.flatMap(d => [
      { type: 'day', day: d, label: dayLabels[d] },
      ...(hasSchedule && schedule.optimal_schedule?.[d]?.length ? [{ type: 'schedule', day: d, label: `${dayLabels[d]} Schedule` }] : []),
    ]),
    { type: 'summary', label: 'Lineup' },
    ...(hasSchedule ? [{ type: 'fullSchedule', label: 'Full Schedule' }] : []),
  ];

  const findInitialCard = () => {
    if (selectedCard !== -1) return selectedCard;
    if (initialCardType) {
      const idx = cards.findIndex(c => c.type === initialCardType.type && (!initialCardType.day || c.day === initialCardType.day));
      if (idx >= 0) return idx;
    }
    const fullIdx = cards.findIndex(c => c.type === 'fullSchedule');
    return fullIdx >= 0 ? fullIdx : cards.length - 1;
  };
  const activeCard = findInitialCard();

  useEffect(() => {
    if (!selectorRef.current) return;
    const idx = activeCard;
    if (idx <= 0) {
      selectorRef.current.scrollLeft = 0;
    } else if (idx >= cards.length - 1) {
      selectorRef.current.scrollLeft = selectorRef.current.scrollWidth;
    } else {
      const cardEl = selectorRef.current.children[idx];
      if (cardEl) {
        const containerWidth = selectorRef.current.offsetWidth;
        selectorRef.current.scrollLeft = cardEl.offsetLeft - (containerWidth / 2) + (cardEl.offsetWidth / 2);
      }
    }
  }, []);

  // Track whether the active card's images have all finished loading
  useEffect(() => {
    setImagesReady(false);
    let cancelled = false;
    const el = cardRefs.current[activeCard];
    if (!el) return;
    waitForImages(el).then(() => { if (!cancelled) setImagesReady(true); });
    return () => { cancelled = true; };
  }, [activeCard]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const el = cardRefs.current[activeCard];
      if (!el) return;
      const [{ domToBlob }, { getLogoDataURLs, toDataURL }] = await Promise.all([
        import('modern-screenshot'),
        import('../logoData.js'),
      ]);
      const logos = await getLogoDataURLs();
      // Pre-inline every non-data-URL <img> src. modern-screenshot handles
      // cross-origin images better than html-to-image, but pre-inlining keeps
      // export deterministic across iOS Safari / Chrome / desktop.
      const allImgs = Array.from(el.querySelectorAll('img'));
      const originals = await Promise.all(allImgs.map(async (img) => {
        const orig = img.getAttribute('src') || '';
        if (!orig || orig.startsWith('data:')) return null;
        let next;
        if (orig.includes('logo-icon')) next = logos.icon;
        else if (orig.includes('logo-text')) next = logos.text;
        else next = await toDataURL(orig);
        return { img, orig, next };
      }));
      const swaps = originals.filter(Boolean);
      await Promise.all(swaps.map(({ img, next }) => new Promise((resolve) => {
        const done = () => { img.removeEventListener('load', done); img.removeEventListener('error', done); resolve(); };
        img.addEventListener('load', done);
        img.addEventListener('error', done);
        img.src = next;
      })));
      await Promise.all(swaps.map(({ img }) => img.decode ? img.decode().catch(() => {}) : Promise.resolve()));
      await waitForImages(el);
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      let blob;
      try {
        blob = await domToBlob(el, { backgroundColor: '#000', scale: 2, width: 390, height: 844 });
      } finally {
        swaps.forEach(({ img, orig }) => { img.src = orig; });
      }
      // Unique filename per share — iOS Messages occasionally drops attachments
      // when it sees a repeated name, so avoid that hazard entirely.
      const shortSlug = festivalConfig.shortName.toLowerCase().replace(/\s+/g, '-');
      const filename = `my-${shortSlug}-lineup-${Date.now()}.png`;
      const file = new File([blob], filename, { type: 'image/png' });
      // Try Capacitor native share first (most reliable in iOS WebView)
      const isNativeApp = window.Capacitor?.isNativePlatform?.();
      // Native: App Store link drives downloads. Web: real festival page.
      const shareUrl = isNativeApp
        ? 'https://apps.apple.com/app/id6763488827'
        : `${APP_URL}/festival`;
      if (isNativeApp) {
        try {
          const [{ Filesystem, Directory }, { Share }] = await Promise.all([
            import('@capacitor/filesystem'),
            import('@capacitor/share'),
          ]);
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          // Cache directory + files[] array makes iOS treat this as image content,
          // unlocking Save Image / Add to Photos / AirDrop image in the share sheet.
          await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
          const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
          await Share.share({
            files: [uri],
            dialogTitle: 'Share your lineup',
          });
          return;
        } catch (nativeErr) {
          console.warn('Native share failed, falling back:', nativeErr);
        }
      }
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        // Keep the URL in the separate `url` field — putting it inside `text`
        // makes iOS Messages generate a link preview and drop the image.
        await navigator.share({
          files: [file],
          title: `My ${festivalConfig.shortName} Lineup`,
          text: `Check out my ${festivalConfig.shortName} lineup on Loud Neighbors! Create your own at`,
          url: shareUrl,
        });
      } else {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl; a.download = filename;
        a.click(); URL.revokeObjectURL(objUrl);
      }
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setExporting(false);
    }
  };

  const ringColor = (s) => s >= 70 ? '#22c55e' : s >= 40 ? '#eab308' : '#ef4444';

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col" onClick={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-safe pb-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <h2 className="text-white font-bold text-sm uppercase tracking-wider">Share Your Lineup</h2>
        <button onClick={onClose} className="text-white/40 hover:text-white p-1">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Preview — mobile sized, scale to fit available space */}
      <div className="flex-1 flex items-center justify-center overflow-hidden min-h-0">
        <div className="relative w-[390px] h-[844px] rounded-3xl overflow-hidden border border-white/10 shadow-2xl origin-center scale-[0.42] sm:scale-[0.48] md:scale-[0.6] lg:scale-[0.7]" style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          {/* Render all cards but only show selected */}
          {cards.map((card, i) => (
            <div
              key={i}
              ref={el => cardRefs.current[i] = el}
              className={`absolute inset-0 ${activeCard === i ? 'block' : 'hidden'}`}
              style={{ width: 390, height: 844 }}
            >
              {card.type === 'score' && (
                <ScoreSlide score={schedule.composite_score} festivalConfig={festivalConfig} />
              )}
              {card.type === 'day' && (() => {
                const artists = schedule.days?.[card.day]?.top5 || [];
                const dayScore = Math.round(schedule.day_scores?.[card.day] || 0);
                const { className: dayBg, style: dayBgStyle } = dayGradientProps(gradients[card.day]);
                return (
                  <div className={`flex items-center justify-center h-full ${dayBg} p-6`} style={dayBgStyle}>
                    <div className="flex flex-col w-full">
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-white/40 text-xs uppercase tracking-[0.3em]">{festivalConfig.headerLabel}</p>
                        <img src="/logo-icon.png" alt="LN" className="h-8 opacity-70" />
                      </div>
                      <div className="flex items-center gap-4 mb-6">
                        <h2 className="text-white font-black text-4xl uppercase">{dayLabels[card.day]}</h2>
                        <div className="relative w-14 h-14">
                          <ScoreRing score={dayScore} size={56} strokeWidth={4} />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-white font-black text-sm">{dayScore}</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        {artists.map((a, j) => (
                          <div key={j} className="flex items-center gap-4">
                            <span className="text-white/40 text-2xl font-black w-8 text-right">{j + 1}</span>
                            {a.artist_image_url ? (
                              <img src={a.artist_image_url} className="w-14 h-14 rounded-full border-2 border-white/20 object-cover" />
                            ) : (
                              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                                {(a.artist_name || '?')[0].toUpperCase()}
                              </div>
                            )}
                            <p className="text-white font-bold text-lg flex-1 leading-tight">{a.artist_name}</p>
                            <ScoreBadge score={a.score} />
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between mt-6">
                        <p className="text-white/40 text-[8px] uppercase tracking-[0.25em]">loudneighbors.com</p>
                        <img src="/logo-text.png" alt="Loud Neighbors" className="h-10 opacity-60" />
                      </div>
                      <p className="text-white/20 text-[6px] text-right mt-1">{festivalConfig.disclaimer}</p>
                    </div>
                  </div>
                );
              })()}
              {card.type === 'schedule' && (
                <ScheduleSlide
                  day={card.day}
                  dayLabel={dayLabels[card.day]}
                  acts={schedule.optimal_schedule?.[card.day] || []}
                  allDayActs={schedule.days?.[card.day]?.all || schedule.days?.[card.day]?.top5 || []}
                  dayScore={Math.round(schedule.day_scores?.[card.day] || 0)}
                  festivalConfig={festivalConfig}
                />
              )}
              {card.type === 'fullSchedule' && (
                <FullScheduleSlide schedule={schedule} festivalConfig={festivalConfig} />
              )}
              {card.type === 'summary' && (
                <SummarySlide schedule={schedule} festivalConfig={festivalConfig} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Card selector + Export */}
      <div className="flex-shrink-0 max-w-lg mx-auto w-full px-4 pb-safe" onClick={e => e.stopPropagation()}>
        <p className="text-white text-xs text-center mb-1">Share to your IG story tag <span className="font-bold">@loudneighbors_</span></p>
        <div className="flex items-center justify-center gap-2 pb-1">
          <svg className="w-4 h-4 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-white/30 text-sm font-bold">Select a highlight to share</span>
          <svg className="w-4 h-4 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </div>
        <div ref={selectorRef} className="flex gap-2 py-2 overflow-x-auto scrollbar-hide px-2">
          {cards.map((card, i) => {
            const isDayCard = card.type === 'day' || card.type === 'schedule';
            const isThemed = card.type === 'fullSchedule' || card.type === 'summary' || card.type === 'score';
            const dayProps = isDayCard ? dayGradientProps(gradients[card.day]) : null;
            const thumbStyle = isThemed
              ? { background: festivalConfig.gradients.narrativeThumb }
              : (dayProps?.style);
            const thumbClass = isDayCard
              ? dayProps.className
              : (isThemed ? '' : 'bg-white/10');
            return (
              <button
                key={i}
                onClick={() => setSelectedCard(i)}
                className={`flex-shrink-0 w-12 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                  activeCard === i ? 'border-white scale-110' : 'border-white/20 opacity-50'
                }`}
              >
                <div
                  className={`w-full h-full flex items-center justify-center text-[6px] font-bold text-white px-0.5 text-center leading-tight ${thumbClass}`}
                  style={thumbStyle}
                >
                  {card.label}
                </div>
              </button>
            );
          })}
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || !imagesReady}
          className="bg-white text-black font-black uppercase tracking-wider text-sm py-3 rounded-full hover:bg-white/90 transition-all disabled:opacity-50 w-full mt-2"
        >
          {exporting ? 'Exporting...' : !imagesReady ? 'Loading images...' : 'Export & Share'}
        </button>
      </div>
    </div>
  );
}

// ── Wrapped-style story viewer ──────────────────────────────────────────────

function WrappedViewer({ schedule, onDone, onShare, onShareCard, festival }) {
  const festivalConfig = getFestivalConfig(festival);
  const [currentSlide, setCurrentSlide] = useState(0);
  const days = ['friday', 'saturday', 'sunday'];
  const dayLabels = { friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

  const hasSchedule = schedule.optimal_schedule && Object.values(schedule.optimal_schedule).some(d => d?.length > 0);
  const slides = [
    { type: 'score' },
    ...days.flatMap(d => [
      { type: 'day', day: d },
      ...(hasSchedule && schedule.optimal_schedule?.[d]?.length ? [{ type: 'schedule', day: d }] : []),
    ]),
    { type: 'summary' },
    ...(hasSchedule ? [{ type: 'fullSchedule' }] : []),
  ];

  const next = () => {
    console.log('NEXT tapped, currentSlide:', currentSlide, 'total:', slides.length);
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(c => c + 1);
    } else {
      console.log('Last slide — opening share');
      onShare();
    }
  };
  const prev = () => {
    console.log('PREV tapped, currentSlide:', currentSlide);
    if (currentSlide > 0) setCurrentSlide(c => c - 1);
  };

  const slide = slides[currentSlide];

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Progress bar */}
      <div className="flex gap-1 px-3 pt-safe pb-2">
        {slides.map((_, i) => (
          <div key={i} className="flex-1 h-0.5 rounded-full overflow-hidden bg-white/10">
            <div
              className={`h-full rounded-full transition-all duration-300 ${i < currentSlide ? 'bg-white w-full' : i === currentSlide ? 'bg-white w-full' : 'w-0'}`}
            />
          </div>
        ))}
      </div>

      {/* Share button — fades in after 1s, only on replay */}
      {onShareCard && <div
        key={`share-btn-${currentSlide}`}
        className="absolute right-4 opacity-0 animate-[fadeIn_0.5s_ease-out_1s_both]"
        style={{ zIndex: 25, top: 'calc(env(safe-area-inset-top, 0px) + 28px)' }}
      >
        <button
          onClick={() => {
            // Map current slide type to share card
            const s = slides[currentSlide];
            if (onShareCard) onShareCard(s.type, s.day);
          }}
          className="bg-white text-black font-bold text-xs px-4 py-1.5 rounded-full hover:bg-white/90 transition-all"
        >
          Share
        </button>
      </div>}

      {/* Slide content */}
      <div className="flex-1 overflow-hidden" key={currentSlide}>
        {slide.type === 'score' && <ScoreSlide score={schedule.composite_score} festivalConfig={festivalConfig} />}
        {slide.type === 'day' && (
          <DaySlide
            day={slide.day}
            dayLabel={dayLabels[slide.day]}
            artists={schedule.days?.[slide.day]?.top5 || []}
            dayScore={Math.round(schedule.day_scores?.[slide.day] || 0)}
            festivalConfig={festivalConfig}
          />
        )}
        {slide.type === 'schedule' && (
          <ScheduleSlide
            day={slide.day}
            dayLabel={dayLabels[slide.day]}
            acts={schedule.optimal_schedule?.[slide.day] || []}
            allDayActs={schedule.days?.[slide.day]?.all || schedule.days?.[slide.day]?.top5 || []}
            dayScore={Math.round(schedule.day_scores?.[slide.day] || 0)}
            festivalConfig={festivalConfig}
          />
        )}
        {slide.type === 'fullSchedule' && <FullScheduleSlide schedule={schedule} festivalConfig={festivalConfig} />}
        {slide.type === 'summary' && <SummarySlide schedule={schedule} festivalConfig={festivalConfig} />}
      </div>

      {/* Navigation hints — tap to continue on slide 0+1, go back only on slide 1 */}
      {currentSlide <= 1 && (
        <div
          key={`hint-next-${currentSlide}`}
          className="absolute bottom-24 md:bottom-20 right-6 flex items-center gap-2 opacity-0 animate-[fadeIn_0.8s_ease-out_2s_both] pointer-events-none"
          style={{ zIndex: 25 }}
        >
          <span className="text-white/50 text-sm font-bold">Tap to continue</span>
          <svg className="w-5 h-5 text-white/50" style={{ animation: 'pulse 1.5s ease-in-out infinite' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      )}
      {currentSlide === 1 && (
        <div
          key={`hint-back-${currentSlide}`}
          className="absolute bottom-24 md:bottom-20 left-6 flex items-center gap-2 opacity-0 animate-[fadeIn_0.8s_ease-out_2s_both] pointer-events-none"
          style={{ zIndex: 25 }}
        >
          <svg className="w-5 h-5 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-white/30 text-sm font-bold">Go back</span>
        </div>
      )}

      {/* Tap zones */}
      <div className="absolute inset-0 top-12 flex" style={{ zIndex: 20 }}>
        <div className="w-1/3 h-full cursor-pointer" onClick={prev} />
        <div className="w-1/3 h-full" />
        <div className="w-1/3 h-full cursor-pointer" onClick={next} />
      </div>

    </div>
  );
}

// ── Results View (after wrapped) ────────────────────────────────────────────

function ResultsView({ schedule, onReplay, onRescore, onShare, onBack }) {
  const [activeDay, setActiveDay] = useState(null);
  const days = ['friday', 'saturday', 'sunday'];
  const dayLabels = { friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
  const barColors = {
    friday: 'bg-gradient-to-r from-orange-500 to-pink-500',
    saturday: 'bg-gradient-to-r from-blue-500 to-cyan-400',
    sunday: 'bg-gradient-to-r from-violet-500 to-fuchsia-400',
  };
  const gradients = {
    friday: 'from-orange-600 via-pink-600 to-purple-700',
    saturday: 'from-blue-600 via-cyan-500 to-teal-500',
    sunday: 'from-violet-600 via-purple-500 to-fuchsia-500',
  };

  // See all — styled like the day replay cards
  if (activeDay) {
    const dayData = schedule.days?.[activeDay];
    const allActs = dayData?.all || dayData?.top5 || [];
    const dayScore = Math.round(schedule.day_scores?.[activeDay] || 0);
    return (
      <div>
        {/* Sticky header: back + day + score */}
        <div className="sticky top-0 z-10 -mx-4 px-4 lg:-mx-8 lg:px-8 pt-2 pb-3" style={{ backgroundColor: 'var(--ln-bg, #000)' }}>
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setActiveDay(null)} className="flex items-center gap-2 text-white/40 hover:text-white text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>
          <div className="flex items-center gap-4">
            <h2 className="text-white font-black text-3xl uppercase">{dayLabels[activeDay]}</h2>
            {dayScore > 0 && (
              <div className="relative w-12 h-12">
                <ScoreRing score={dayScore} size={48} strokeWidth={3} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white font-black text-xs">{dayScore}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6">
          {allActs.map((a, i) => (
            <div key={a.artist_spotify_id || i} className="flex items-center gap-3 py-2">
              <span className="text-white/20 text-sm font-black w-6 text-right">{a.rank}</span>
              {a.artist_image_url ? (
                <img src={a.artist_image_url} alt="" className="w-10 h-10 rounded-full border border-white/10 object-cover" loading="lazy" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/30 font-bold text-xs">
                  {(a.artist_name || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm leading-tight">{a.artist_name}</p>
              </div>
              <ScoreBadge score={a.score} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-results-card className="space-y-6">
      {/* Title + score + actions */}
      <div className="flex items-center gap-4">
        <h2 className="text-white font-black text-lg uppercase">Schedule</h2>
        <div className="relative w-12 h-12 flex-shrink-0">
          <ScoreRing score={schedule.composite_score} size={48} strokeWidth={3} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white font-black text-sm">{schedule.composite_score}</span>
          </div>
        </div>
        <div className="flex gap-3 ml-auto items-center">
          <button onClick={onReplay} className="text-white/30 text-xs hover:text-white/50">Replay</button>
          <button onClick={onRescore} className="text-white/30 text-xs hover:text-white/50">Rescore</button>
          <button onClick={onShare} className="bg-white text-black font-bold text-xs px-4 py-1.5 rounded-full hover:bg-white/90">Share</button>
        </div>
      </div>

      {/* Day sections with score bars */}
      {days.map(day => {
        const data = schedule.days?.[day];
        if (!data) return null;
        const top5 = data.top5 || [];
        const dayScore = Math.round(schedule.day_scores?.[day] || 0);
        return (
          <div key={day}>
            {/* Day name + score bar + see all */}
            <div className="flex items-center gap-3 mb-3">
              <p className="text-white font-black text-sm uppercase w-20 flex-shrink-0">{dayLabels[day]}</p>
              <div className="flex-1 h-3 bg-white/10 overflow-hidden">
                <div className={`h-full ${barColors[day]}`} style={{ width: `${dayScore}%` }} />
              </div>
              <span className="text-white font-black text-sm w-8 text-right">{dayScore}</span>
              <button onClick={() => setActiveDay(day)} className="text-white/30 text-xs hover:text-white/50 flex-shrink-0">See all</button>
            </div>
            {/* Top 5 artist images */}
            <div className="grid grid-cols-5 gap-4">
              {top5.map((a, i) => (
                <div key={a.artist_spotify_id || i} className="flex flex-col items-center">
                  <div className="relative w-14 h-14 md:w-20 md:h-20">
                    {a.artist_image_url ? (
                      <img src={a.artist_image_url} alt="" className="w-full h-full object-cover bg-white/5 rounded-full" loading="lazy" />
                    ) : (
                      <div className="w-full h-full rounded-full bg-white/5 flex items-center justify-center text-white/20 text-sm font-black">
                        {(a.artist_name || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2"><ScoreBadge score={a.score} /></div>
                  </div>
                  <p className="text-white font-medium text-[10px] md:text-xs mt-2 text-center leading-tight">{a.artist_name}</p>
                </div>
              ))}
            </div>

            {/* Optimal Schedule */}
            {schedule.optimal_schedule?.[day]?.length > 0 && (
              <div className="mt-4">
                <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-2">Your Schedule</p>
                <div className="space-y-1.5">
                  {[...schedule.optimal_schedule[day]].reverse().map((a, i) => (
                    <div key={a.act_id || i} className="flex items-center gap-3 bg-white/5 rounded-xl px-3 py-2">
                      {a.artist_image_url ? (
                        <img src={a.artist_image_url} alt="" className="w-9 h-9 rounded-full border border-white/10 object-cover flex-shrink-0" loading="lazy" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/30 font-bold text-xs flex-shrink-0">
                          {(a.artist_name || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm leading-tight truncate">{a.artist_name}</p>
                        <p className="text-white/40 text-[11px]">
                          {a.stage} &middot; {formatTime(a.set_start)}{a.set_end ? ` – ${formatTime(a.set_end)}` : ''}
                        </p>
                      </div>
                      <ScoreBadge score={a.score} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Lineup View (pre-score) ─────────────────────────────────────────────────

function LineupView({ acts, onScore, scoring, hasListeningData = true, onCreateCustom, customSchedule, onViewCustom, festivalLabel }) {
  const days = ['friday', 'saturday', 'sunday'];
  const dayLabelsLong = { friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

  return (
    <div>
      <div className="text-center pb-6 mb-4">
        <h1 className="text-white font-black text-3xl md:text-4xl uppercase tracking-tight mb-2">{festivalLabel || `Coachella ${YEAR}`}</h1>
        <p className="text-white/40 text-sm">Find your perfect lineup based on your listening</p>
      </div>

      {/* TODO: uncomment to show acts by day
      {days.map(day => {
        const dayActs = acts.filter(a => a.day === day);
        if (!dayActs.length) return null;
        const headliner = dayActs[0];
        const rest = dayActs.slice(1);
        return (
          <div key={day} className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-white/40 text-[10px] uppercase tracking-[0.3em]">
                {dayLabelsLong[day]}
              </span>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <div className="text-center mb-3">
              <div className="flex items-center justify-center gap-3">
                {headliner.artist_image_url && (
                  <img src={headliner.artist_image_url} className="w-10 h-10 rounded-full ring-2 ring-white/20" loading="lazy" />
                )}
                <span className="text-white font-black text-3xl md:text-4xl uppercase">{headliner.artist_name}</span>
              </div>
            </div>
            <div className="text-center leading-relaxed">
              {rest.map((act, i) => (
                <React.Fragment key={act.id || i}>
                  {i > 0 && <span className="text-white/20 mx-1">&middot;</span>}
                  <span className={`text-white/${i < 8 ? '70' : i < 20 ? '50' : '30'} ${i < 8 ? 'text-sm font-bold' : 'text-xs'}`}>
                    {act.artist_name}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })}
      */}

      <div className="text-center py-8">
        {hasListeningData === null ? (
          <>
            <div className="inline-block bg-transparent rounded-full px-10 py-4">
              <span className="text-transparent font-black uppercase tracking-wider text-sm">Find My Perfect Lineup</span>
            </div>
            <p className="text-transparent text-xs mt-3">Scored based on your Spotify listening</p>
          </>
        ) : (
          <>
            <button
              onClick={onScore}
              disabled={scoring || !hasListeningData}
              className={`font-black uppercase tracking-wider text-sm px-10 py-4 rounded-full transition-all disabled:opacity-50 shadow-[0_0_40px_rgba(255,255,255,0.15)] ${
                hasListeningData ? 'bg-white text-black hover:bg-white/90' : 'bg-white/20 text-white/40 cursor-not-allowed shadow-none'
              }`}
            >
              {scoring ? 'Building Your Lineup...' : 'Find My Perfect Lineup'}
            </button>
            {hasListeningData ? (
              <p className="text-white/20 text-xs mt-3">Scored based on your Spotify listening</p>
            ) : (
              <p className="text-white/40 text-xs mt-3">Not enough Spotify data yet — listen to more music and come back!</p>
            )}
          </>
        )}
      </div>

      {/* Your Schedules section */}
      <div className="max-w-md mx-auto mb-6 px-4">
        <h3 className="text-white font-black text-sm uppercase tracking-wider mb-3">Your Schedules</h3>

        {/* Create Optimal */}
        <button
          onClick={hasListeningData ? onScore : undefined}
          disabled={!hasListeningData || scoring}
          className={`w-full flex items-center gap-3 border rounded-xl px-4 py-3 mb-2 transition-all text-left ${
            hasListeningData ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white/[0.02] border-white/5 opacity-50 cursor-not-allowed'
          }`}
        >
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm">Create Your Optimal Schedule</p>
            <p className="text-white/40 text-xs">{hasListeningData ? 'Based on your music taste' : 'Not enough listening data'}</p>
          </div>
          <svg className="w-4 h-4 text-white/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Custom schedule (if exists) */}
        {customSchedule?.has_custom && onViewCustom && (
          <button
            onClick={onViewCustom}
            className="w-full flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 mb-2 hover:bg-white/10 transition-all text-left"
          >
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white font-bold flex-shrink-0">
              <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm">Your Custom Schedule</p>
              <p className="text-white/40 text-xs">{Object.values(customSchedule.custom_schedule || {}).flat().length} acts selected</p>
            </div>
            <svg className="w-4 h-4 text-white/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Create custom */}
        {!customSchedule?.has_custom && onCreateCustom && (
          <button
            onClick={onCreateCustom}
            className="w-full flex items-center gap-3 bg-white/5 border border-white/10 border-dashed rounded-xl px-4 py-3 hover:bg-white/10 transition-all text-left"
          >
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm">Create Your Own Schedule</p>
              <p className="text-white/40 text-xs">Build a custom lineup</p>
            </div>
            <svg className="w-4 h-4 text-white/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// ── Scoring Animation ──────────────────────────────────────────────────────

function ScoringAnimation({ acts, scoreData, onSkip, onDone, festival }) {
  const festivalConfig = getFestivalConfig(festival);
  // Phases: 0=scanning lineup, 1=integrating DNA, 2=friday, 3=saturday, 4=sunday
  const [phase, setPhase] = useState(0);
  const [visibleArtist, setVisibleArtist] = useState(-1);
  const [dnaVisible, setDnaVisible] = useState(0);
  const [barFill, setBarFill] = useState(0);
  const [showArtists, setShowArtists] = useState(false);

  const days = ['friday', 'saturday', 'sunday'];
  const dayLabels = { friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
  const barColors = festivalConfig.dayBarColors;
  const bgGradients = festivalConfig.animationBackgrounds;

  // Weighted random artists — later set times (headliners) more likely to appear
  const sampleArtists = useMemo(() => {
    const withImages = acts.filter(a => a.artist_name && a.artist_image_url && a.set_start);
    // Weight by set time — later = higher weight (headliners play later)
    const weighted = withImages.map(a => {
      const h = new Date(a.set_start).getHours();
      const hour = h < 6 ? h + 24 : h; // after midnight = late
      const weight = Math.pow(hour - 12, 2); // quadratic weight, 1PM=1, midnight=144
      return { ...a, weight };
    });
    // Weighted shuffle
    const shuffled = [];
    const pool = [...weighted];
    while (shuffled.length < 20 && pool.length > 0) {
      const totalWeight = pool.reduce((s, a) => s + a.weight, 0);
      let r = Math.random() * totalWeight;
      for (let i = 0; i < pool.length; i++) {
        r -= pool[i].weight;
        if (r <= 0) {
          shuffled.push(pool.splice(i, 1)[0]);
          break;
        }
      }
    }
    return shuffled.map(a => ({ name: a.artist_name, image: a.artist_image_url }));
  }, [acts]);

  // Phase 0: Scan lineup artists
  useEffect(() => {
    if (phase !== 0) return;
    const count = Math.min(sampleArtists.length, 15);
    let i = 0;
    const interval = setInterval(() => {
      setVisibleArtist(i);
      i++;
      if (i >= count) {
        clearInterval(interval);
        setTimeout(() => setPhase(1), 600);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [phase, sampleArtists.length]);

  // Phase 1: Integrating DNA text
  useEffect(() => {
    if (phase !== 1) return;
    const labels = ['top artists', 'genre preferences', 'related artists', 'listening history'];
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDnaVisible(i);
      if (i >= labels.length) {
        clearInterval(interval);
        // Wait for score data before proceeding
        const waitForData = () => {
          if (scoreData) {
            setTimeout(() => setPhase(2), 800);
          } else {
            setTimeout(waitForData, 200);
          }
        };
        setTimeout(waitForData, 1000);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [phase, scoreData]);

  // Phases 2-4: Day reveal (bar fills, then artists appear)
  const currentDay = phase >= 2 && phase <= 4 ? days[phase - 2] : null;
  const dayScore = currentDay && scoreData ? Math.round(scoreData.day_scores?.[currentDay] || 0) : 0;
  const dayTop5 = currentDay && scoreData ? (scoreData.days?.[currentDay]?.top5 || []) : [];

  useEffect(() => {
    if (phase < 2 || phase > 4 || !scoreData) return;
    setBarFill(0);
    setShowArtists(false);
    // Animate bar
    let frame = 0;
    const totalFrames = 40;
    const interval = setInterval(() => {
      frame++;
      const eased = 1 - Math.pow(1 - frame / totalFrames, 3);
      setBarFill(Math.round(dayScore * eased));
      if (frame >= totalFrames) {
        clearInterval(interval);
        // Show top artists
        setTimeout(() => setShowArtists(true), 300);
        // Move to next phase
        setTimeout(() => {
          if (phase < 4) {
            setPhase(p => p + 1);
          } else {
            // All done
            if (onDone) onDone();
          }
        }, 3500);
      }
    }, 50);
    return () => clearInterval(interval);
  }, [phase, scoreData]);

  const phaseBg = currentDay
    ? bgGradients[currentDay]
    : phase === 0
      ? bgGradients.scanning
      : phase === 1
        ? bgGradients.integrating
        : '#000';

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center px-6 transition-all duration-1000 overflow-hidden"
      style={{
        background: phaseBg,
        backgroundSize: '200% 200%',
        animation: 'ln-bg-pan 12s ease-in-out infinite',
      }}
    >
      {/* Phase 0: Scanning lineup */}
      {phase === 0 && (
        <div className="flex flex-col items-center">
          <p className="text-white/40 text-xs uppercase tracking-[0.3em] mb-6 animate-[fadeIn_0.5s_ease-out]">Scanning lineup</p>
          <div className="relative w-full h-[220px] flex items-center justify-center">
            {sampleArtists.slice(0, 15).map((a, i) => (
              <div
                key={i}
                className="absolute flex flex-col items-center transition-all duration-500"
                style={{
                  opacity: visibleArtist === i ? 1 : visibleArtist > i ? 0.03 : 0,
                  transform: visibleArtist === i ? 'scale(1) translateY(0)' : 'scale(0.7) translateY(15px)',
                }}
              >
                <img src={a.image} alt="" className="w-28 h-28 rounded-full border-4 border-white/10 object-cover shadow-2xl" />
                <p className="text-white font-bold text-lg mt-3 text-center max-w-[200px] leading-tight">{a.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase 1: Integrating DNA */}
      {phase === 1 && (
        <div className="flex flex-col items-center animate-[fadeIn_0.5s_ease-out]">
          <p className="text-white/40 text-xs uppercase tracking-[0.3em] mb-2">Integrating with your</p>
          <p className="text-white font-black text-2xl uppercase mb-6">Music DNA</p>
          <div className="flex gap-3 flex-wrap justify-center max-w-xs">
            {['top artists', 'genre preferences', 'related artists', 'listening history'].map((label, i) => (
              <span
                key={label}
                className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-500"
                style={{
                  opacity: i < dnaVisible ? 1 : 0,
                  transform: i < dnaVisible ? 'scale(1)' : 'scale(0.5)',
                  backgroundColor: i < dnaVisible ? 'rgba(255,255,255,0.08)' : 'transparent',
                  borderColor: i < dnaVisible ? 'rgba(255,255,255,0.15)' : 'transparent',
                  color: i < dnaVisible ? 'rgba(255,255,255,0.6)' : 'transparent',
                }}
              >
                {label}
              </span>
            ))}
          </div>
          {!scoreData && (
            <div className="mt-8 flex gap-2">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-1.5 h-1.5 rounded-full bg-white/20 animate-pulse" style={{ animationDelay: `${i * 0.3}s` }} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Phases 2-4: Day reveal */}
      {phase >= 2 && phase <= 4 && currentDay && (
        <div className="w-full max-w-sm animate-[fadeIn_0.5s_ease-out]" key={currentDay}>
          <p className="text-white/40 text-xs uppercase tracking-[0.3em] mb-6 text-center">Building your schedule</p>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-black text-2xl uppercase">{dayLabels[currentDay]}</span>
              <span className="text-white font-black text-2xl">{barFill}</span>
            </div>
            <div className="h-4 bg-white/10 rounded-full overflow-hidden">
              <div
                className={`h-full ${barColors[currentDay]} rounded-full transition-all duration-75`}
                style={{ width: `${barFill}%` }}
              />
            </div>
          </div>

          {/* Top 5 artists for this day */}
          {showArtists && (
            <div className="grid grid-cols-5 gap-3">
              {dayTop5.slice(0, 5).map((a, i) => (
                <div
                  key={a.artist_spotify_id || i}
                  className="flex flex-col items-center animate-[fadeIn_0.4s_ease-out_both]"
                  style={{ animationDelay: `${i * 0.15}s` }}
                >
                  <div className="relative">
                    {a.artist_image_url ? (
                      <img src={a.artist_image_url} alt="" className="w-14 h-14 rounded-full border-2 border-white/20 object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-white font-bold text-sm">
                        {(a.artist_name || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2"><ScoreBadge score={a.score} /></div>
                  </div>
                  <p className="text-white font-medium text-[9px] mt-2 text-center leading-tight truncate w-full">{a.artist_name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tap anywhere to skip */}
      {onSkip && (
        <>
          <div className="absolute inset-0" style={{ zIndex: 30 }} onClick={onSkip} />
          <div className="absolute bottom-24 md:bottom-12 left-0 right-0 text-center pointer-events-none opacity-0 animate-[fadeIn_0.5s_ease-out_2s_both]" style={{ zIndex: 31 }}>
            <span className="text-white/30 text-sm font-bold">Tap anywhere to skip</span>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function CoachellaPage() {
  const { user } = useLNAuth();
  const location = useLocation();
  const [schedule, setSchedule] = useState(null);
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);
  const [showScoringAnim, setShowScoringAnim] = useState(false);
  const [pendingScoreData, setPendingScoreData] = useState(null);
  const [showWrapped, setShowWrapped] = useState(false);
  const [wrappedKey, setWrappedKey] = useState(0);
  const [showShare, setShowShare] = useState(false);
  const [shareInitialCard, setShareInitialCard] = useState(null);
  const [shareScheduleData, setShareScheduleData] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderInitialIds, setBuilderInitialIds] = useState(null);
  const [customSchedule, setCustomSchedule] = useState(null);
  const [showCustomResults, setShowCustomResults] = useState(false);
  const [groups, setGroups] = useState([]);
  const [showGroupCreate, setShowGroupCreate] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [activeGroup, setActiveGroup] = useState(null); // { group, optimal, custom, ... }
  const [groupScheduleType, setGroupScheduleType] = useState('optimal');
  const [showGroupBuilder, setShowGroupBuilder] = useState(false);
  const [isReplay, setIsReplay] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { groupId, isOnly }
  const [maxGroupsModal, setMaxGroupsModal] = useState(null); // { inviteCode } — shown when join fails due to 5 group limit
  const [hasListeningData, setHasListeningData] = useState(null); // null = checking, true/false = result
  const [festival, setFestival] = useState(null); // null = show picker, string = show festival
  const [festivals, setFestivals] = useState([]);

  // Hide bottom nav during full-screen story views
  useEffect(() => {
    const fullscreen = showScoringAnim || showWrapped || showShare;
    document.body.classList.toggle('ln-fullscreen', fullscreen);
    return () => document.body.classList.remove('ln-fullscreen');
  }, [showScoringAnim, showWrapped, showShare]);

  useEffect(() => {
    const goFestival = () => {
      setFestival(null);
      setShowResults(false);
      setShowWrapped(false);
      setShowShare(false);
      setShowScoringAnim(false);
      setShowBuilder(false);
      setShowCustomResults(false);
      setActiveGroup(null);
      setShowGroupBuilder(false);
      setShowGroupCreate(false);
      // Refresh festivals to reflect any new scores/custom schedules
      getFestivals(YEAR).then(d => setFestivals(d.festivals || [])).catch(() => {});
    };
    window.addEventListener('ln-go-festival', goFestival);
    return () => window.removeEventListener('ln-go-festival', goFestival);
  }, []);

  const [pendingGroupId, setPendingGroupId] = useState(null); // group to auto-open after festival loads

  // Effect 1: Load festivals on mount, handle group invite
  useEffect(() => {
    const loadFestivals = async () => {
      setLoading(true);
      try {
        const festData = await getFestivals(YEAR);
        setFestivals(festData.festivals || []);
      } catch {}

      // Check for group invite in URL or sessionStorage
      const params = new URLSearchParams(window.location.search);
      const groupCode = params.get('group') || sessionStorage.getItem('ln_group_invite');
      if (groupCode) {
        sessionStorage.removeItem('ln_group_invite');
        try {
          const joinResult = await joinGroup(groupCode);
          if (joinResult.group) {
            // Set festival from the group's festival_name and queue auto-open
            // Keep loading=true so Effect 2 shows spinner until data is ready
            setFestival(joinResult.group.festival_name || 'coachella_weekend_1');
            setPendingGroupId(joinResult.group.id);
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }
        } catch (err) {
          if (err?.message?.includes('max') || err?.message?.includes('5 groups') || err?.message?.includes('groups per festival')) {
            setMaxGroupsModal({ inviteCode: groupCode });
          }
          window.history.replaceState({}, '', window.location.pathname);
        }
      }

      // If coming from festival landing / onboarding
      const autoScore = sessionStorage.getItem('ln_auto_score_festival');
      if (autoScore) {
        const selectedFestival = sessionStorage.getItem('ln_selected_festival') || 'coachella_weekend_1';
        const pendingGroup = sessionStorage.getItem('ln_pending_group_id');
        sessionStorage.removeItem('ln_selected_festival');
        if (pendingGroup) {
          sessionStorage.removeItem('ln_pending_group_id');
          setPendingGroupId(parseInt(pendingGroup, 10));
        }
        // Keep loading=true — Effect 2 will handle it when festival changes
        setFestival(selectedFestival);
        return;
      }
      setLoading(false);
    };
    loadFestivals();
  }, [location.search]);

  // Effect 2: Load festival data when festival is selected
  useEffect(() => {
    if (!festival) return;
    // Show loading immediately to avoid flash of empty content
    setLoading(true);
    // Check for auto-score BEFORE resetting state
    const autoScore = sessionStorage.getItem('ln_auto_score_festival');
    if (autoScore) {
      sessionStorage.removeItem('ln_auto_score_festival');
      setShowScoringAnim(true);
    } else {
      setShowScoringAnim(false);
    }
    // Reset state after loading is set
    setSchedule(null);
    setCustomSchedule(null);
    setActs([]);
    setGroups([]);
    setShowResults(false);
    setShowBuilder(false);
    setShowCustomResults(false);
    setShowWrapped(false);
    setActiveGroup(null);
    setShowGroupBuilder(false);
    setShowGroupCreate(false);
    setHasListeningData(null);
    const load = async () => {
      try {
        const data = await getCoachellaSchedule(YEAR, festival);
        if (data.scored) {
          setSchedule(data);
          setShowScoringAnim(false);
        } else if (autoScore) {
          // Auto-score: animation is already showing, now do the actual scoring
          setPendingScoreData(null);
          try {
            const artistCheck = await getTopArtists('short_term', 1, { fallback: true });
            if (!artistCheck.items || artistCheck.items.length === 0) {
              setHasListeningData(false);
              setShowScoringAnim(false);
            } else {
              try {
                const result = await scoreCoachellaLineup(YEAR, festival);
                setPendingScoreData({ scored: true, ...result });
              } catch {
                setShowScoringAnim(false);
              }
            }
          } catch {
            setHasListeningData(false);
            setShowScoringAnim(false);
          }
        }
        const actsData = await getCoachellaActs(YEAR, null, festival);
        setActs(actsData.acts || []);
      } catch {
        try {
          const actsData = await getCoachellaActs(YEAR, null, festival);
          setActs(actsData.acts || []);
        } catch {}
      }
      // Check if user has any listening data (for disabling score button)
      try {
        const artists = await getTopArtists('short_term', 1, { fallback: true });
        setHasListeningData(!!(artists.items && artists.items.length > 0));
      } catch {
        setHasListeningData(false);
      }
      // Load custom schedule in background
      try {
        const customData = await getCustomSchedule(YEAR, festival);
        if (customData.has_custom) setCustomSchedule(customData);
      } catch {}
      // Load groups
      try {
        const groupsData = await getGroups(festival);
        setGroups(groupsData.groups || []);
      } catch {}
      // Auto-open group if we just joined via invite link
      if (pendingGroupId) {
        try {
          const grpData = await getGroupSchedule(YEAR, pendingGroupId, festival);
          setActiveGroup(grpData);
        } catch {}
        setPendingGroupId(null);
      }
      setLoading(false);
    };
    load();
  }, [festival]);

  const handleSaveCustom = async (selectedActs) => {
    try {
      const result = await saveCustomSchedule(YEAR, selectedActs, festival);
      setCustomSchedule(result);
      setShowBuilder(false);
      setBuilderInitialIds(null);
      // Open share/export with custom schedule data
      const cs = result;
      setShareScheduleData({
        scored: true,
        composite_score: cs.composite_score || 0,
        day_scores: cs.day_scores || {},
        days: Object.fromEntries(['friday', 'saturday', 'sunday'].map(d => [d, {
          top5: (cs.custom_schedule?.[d] || []).sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 5),
          all: cs.custom_schedule?.[d] || [],
        }])),
        optimal_schedule: cs.custom_schedule || {},
      });
      setShowShare(true);
    } catch (err) {
      console.error('Failed to save custom schedule:', err);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) return;
    try {
      const result = await createGroup(groupName.trim(), festival);
      const group = result.group || result;
      // Compute initial schedule
      await computeGroupSchedule(YEAR, group.id, festival);
      const grpData = await getGroupSchedule(YEAR, group.id, festival);
      setActiveGroup(grpData);
      setShowGroupCreate(false);
      setGroupName('');
      // Refresh groups list
      const groupsData = await getGroups(festival);
      setGroups(groupsData.groups || []);
      // Share invite
      const inviteUrl = `${APP_URL}/festival?group=${group.invite_code}`;
      if (navigator.share) {
        try {
          await navigator.share({
            text: `Join my ${FESTIVAL_DISPLAY[festival] || festival} group on Loud Neighbors!\n\nFind the perfect group schedule based on everyone's music taste.\n\n${inviteUrl}`,
          });
        } catch {}
      } else {
        navigator.clipboard?.writeText(`Join my ${FESTIVAL_DISPLAY[festival] || festival} group on Loud Neighbors!\n\nFind the perfect group schedule based on everyone's music taste.\n\n${inviteUrl}`);
        alert('Invite link copied!');
      }
    } catch (err) {
      console.error('Failed to create group:', err);
    }
  };

  const handleViewGroup = async (groupId) => {
    try {
      const grpData = await getGroupSchedule(YEAR, groupId, festival);
      setActiveGroup(grpData);
      setGroupScheduleType('optimal');
    } catch (err) {
      console.error('Failed to load group schedule:', err);
    }
  };

  const handleSaveGroupCustom = async (selectedActs) => {
    if (!activeGroup?.group?.id) return;
    try {
      await saveGroupCustomSchedule(YEAR, activeGroup.group.id, selectedActs, festival);
      const grpData = await getGroupSchedule(YEAR, activeGroup.group.id, festival);
      setActiveGroup(grpData);
      setShowGroupBuilder(false);
      setGroupScheduleType('custom');
    } catch (err) {
      console.error('Failed to save group custom:', err);
    }
  };

  const handleScore = async () => {
    setScoring(true);
    setShowScoringAnim(true);
    setPendingScoreData(null);
    try {
      const result = await scoreCoachellaLineup(YEAR, festival);
      setPendingScoreData({ scored: true, ...result });
      // Animation will call onDone when it finishes all phases
    } catch (err) {
      console.error('Scoring failed:', err);
      setShowScoringAnim(false);
    } finally {
      setScoring(false);
    }
  };

  const handleAnimDone = () => {
    setShowScoringAnim(false);
    if (pendingScoreData) {
      setSchedule(pendingScoreData);
      setPendingScoreData(null);
    }
    setWrappedKey(k => k + 1);
    setShowWrapped(true);
  };

  // Confirm modal for leave/delete group
  if (confirmModal) {
    const isOnly = confirmModal.isOnly;
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-6" onClick={() => setConfirmModal(null)}>
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
          <h3 className="text-white font-black text-lg mb-2">{isOnly ? 'Delete Group' : 'Leave Group'}</h3>
          <p className="text-white/50 text-sm mb-6">
            {isOnly
              ? 'You are the only member. This will permanently delete the group and its schedules.'
              : 'Are you sure you want to leave this group? You can rejoin later with an invite link.'}
          </p>
          <div className="flex gap-3">
            <button
              onClick={async () => {
                try {
                  await leaveGroup(confirmModal.groupId);
                  if (confirmModal.fromGroupView) setActiveGroup(null);
                  const groupsData = await getGroups(festival);
                  setGroups(groupsData.groups || []);
                } catch (err) { console.error(err); }
                setConfirmModal(null);
              }}
              className="flex-1 bg-red-500 text-white font-bold text-sm py-2.5 rounded-xl hover:bg-red-600 transition-colors"
            >
              {isOnly ? 'Delete' : 'Leave'}
            </button>
            <button
              onClick={() => setConfirmModal(null)}
              className="flex-1 bg-white/10 text-white font-bold text-sm py-2.5 rounded-xl hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Max groups modal — shown when trying to join but already in 5 groups
  if (maxGroupsModal) {
    const selectedGroupToLeave = maxGroupsModal.selectedGroupId;
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-6" onClick={() => setMaxGroupsModal(null)}>
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
          <h3 className="text-white font-black text-lg mb-2">Group Limit Reached</h3>
          <p className="text-white/50 text-sm mb-4">You're already in 5 groups for this festival. Select a group to leave so you can join the new one.</p>
          {groups.length > 0 && (
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {groups.map(g => (
                <button
                  key={g.id}
                  onClick={() => setMaxGroupsModal({ ...maxGroupsModal, selectedGroupId: g.id })}
                  className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-all ${
                    selectedGroupToLeave === g.id
                      ? 'bg-white/15 border-2 border-white/30'
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm">{g.name}</p>
                    <p className="text-white/40 text-xs">{g.member_count} member{g.member_count !== 1 ? 's' : ''}</p>
                  </div>
                  {selectedGroupToLeave === g.id && (
                    <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-3">
            <button
              disabled={!selectedGroupToLeave}
              onClick={async () => {
                if (!selectedGroupToLeave) return;
                try {
                  await leaveGroup(selectedGroupToLeave);
                  const updatedGroups = await getGroups(festival);
                  setGroups(updatedGroups.groups || []);
                  const joinResult = await joinGroup(maxGroupsModal.inviteCode);
                  if (joinResult.group) {
                    const grpGroups = await getGroups(festival);
                    setGroups(grpGroups.groups || []);
                    if (schedule?.scored) {
                      try {
                        const grpData = await getGroupSchedule(YEAR, joinResult.group.id, festival);
                        setActiveGroup(grpData);
                      } catch {}
                    }
                  }
                  setMaxGroupsModal(null);
                } catch (err) {
                  console.error('Failed to leave/rejoin:', err);
                }
              }}
              className={`flex-1 font-bold text-sm py-2.5 rounded-xl transition-all ${
                selectedGroupToLeave
                  ? 'bg-white text-black hover:bg-white/90'
                  : 'bg-white/10 text-white/30 cursor-not-allowed'
              }`}
            >
              Leave & Join
            </button>
            <button
              onClick={() => setMaxGroupsModal(null)}
              className="flex-1 bg-white/10 text-white font-bold text-sm py-2.5 rounded-xl hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Scoring animation overlay
  if (showScoringAnim) {
    return <ScoringAnimation
      acts={acts}
      scoreData={pendingScoreData}
      festival={festival}
      onDone={handleAnimDone}
      onSkip={schedule?.scored ? () => {
        setShowScoringAnim(false);
        if (pendingScoreData) {
          setSchedule(pendingScoreData);
          setPendingScoreData(null);
          setShowWrapped(true);
        } else if (window._lnSkipToWrapped) {
          window._lnSkipToWrapped();
          window._lnSkipToWrapped = null;
        }
      } : null}
    />;
  }

  // Share modal overlay
  if (showShare && (schedule?.scored || shareScheduleData)) {
    return <ShareModal schedule={shareScheduleData || schedule} initialCardType={shareInitialCard} festival={festival} onClose={() => { setShowShare(false); setShareInitialCard(null); setShareScheduleData(null); }} />;
  }

  // Wrapped story overlay
  if (showWrapped && schedule?.scored) {
    return <WrappedViewer key={wrappedKey} schedule={schedule} festival={festival} onDone={() => { setShowWrapped(false); setIsReplay(false); getGroups(festival).then(d => setGroups(d.groups || [])).catch(() => {}); }} onShare={() => { setShowWrapped(false); setShowShare(true); setIsReplay(false); }} onShareCard={isReplay ? (type, day) => {
      setShareInitialCard({ type, day });
      setShowWrapped(false);
      setShowShare(true);
      setIsReplay(false);
    } : null} />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden">
        <MobileHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-white/20 animate-pulse" />
        </div>
      </div>
    );
  }

  // Festival picker — show when no festival is selected
  if (!festival && festivals.length > 0) {
    const yourFestivals = festivals.filter(f => f.scored || f.has_custom);
    const otherFestivals = festivals.filter(f => !f.scored && !f.has_custom);

    return (
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* header */}
        <MobileHeader />
        <main className="flex-1 overflow-y-auto px-4 lg:px-8 pb-nav md:pb-8 md:pt-6">
          {yourFestivals.length > 0 && (
            <div className="max-w-md mx-auto mb-8">
              <h3 className="text-white font-black text-sm uppercase tracking-wider mb-3">Your Festivals</h3>
              {yourFestivals.map(f => (
                <button
                  key={f.festival_name}
                  onClick={() => { setLoading(true); setFestival(f.festival_name); }}
                  className="w-full flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 mb-2 hover:bg-white/10 transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/20 to-purple-600/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-white/60 text-lg">{'\u{1F3AA}'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm">{FESTIVAL_DISPLAY[f.festival_name] || f.display_name}</p>
                  </div>
                  <svg className="w-4 h-4 text-white/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          )}

          {otherFestivals.length > 0 && (
            <div className="max-w-md mx-auto mb-8">
              <h3 className="text-white font-black text-sm uppercase tracking-wider mb-3">All Festivals</h3>
              {otherFestivals.map(f => (
                <button
                  key={f.festival_name}
                  onClick={() => { setLoading(true); setFestival(f.festival_name); }}
                  className="w-full flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 mb-2 hover:bg-white/10 transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-white/40 text-lg">{'\u{1F3AA}'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm">{FESTIVAL_DISPLAY[f.festival_name] || f.display_name}</p>
                  </div>
                  <svg className="w-4 h-4 text-white/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          )}

          {festivals.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-white/20 text-sm">No festivals available yet</p>
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <MobileHeader />

      <div className="flex-1 overflow-y-auto px-4 lg:px-8 md:pt-0 pb-nav md:pb-8 ">
        {showGroupBuilder && activeGroup ? (
          <ScheduleBuilder
            acts={(() => {
              // Merge group avg scores into acts
              const scoreMap = {};
              const optSched = activeGroup.optimal?.schedule || {};
              for (const day of ['friday', 'saturday', 'sunday']) {
                for (const a of (optSched[day] || [])) {
                  if (a.artist_spotify_id) scoreMap[a.artist_spotify_id] = a.score || 0;
                }
              }
              return acts.map(a => ({ ...a, score: scoreMap[a.artist_spotify_id] || a.score || 0 }));
            })()}
            optimalSchedule={activeGroup.optimal?.schedule}
            initialSelectedIds={activeGroup.custom?.schedule ? Object.values(activeGroup.custom.schedule).flat().map(a => a.act_id).filter(Boolean) : null}
            onComplete={handleSaveGroupCustom}
            onCancel={() => setShowGroupBuilder(false)}
          />
        ) : activeGroup ? (
          /* Group schedule view */
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <button onClick={() => setActiveGroup(null)} className="text-white/40 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-white font-black text-lg uppercase">{activeGroup.group?.name}</h2>
              <div className="relative w-10 h-10 flex-shrink-0">
                <ScoreRing score={(activeGroup.optimal)?.composite_score || 0} size={40} strokeWidth={3} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white font-black text-[10px]">{(activeGroup.optimal)?.composite_score || 0}</span>
                </div>
              </div>
              <div className="flex gap-2 ml-auto">
                <button onClick={() => {
                  const inviteUrl = `${APP_URL}/festival?group=${activeGroup.group?.invite_code}`;
                  if (navigator.share) {
                    navigator.share({
                      text: `Join my ${FESTIVAL_DISPLAY[festival] || festival} group on Loud Neighbors!\n\nFind the perfect group schedule based on everyone's music taste.\n\n${inviteUrl}`,
                    }).catch(() => {});
                  } else {
                    navigator.clipboard?.writeText(`Join my ${FESTIVAL_DISPLAY[festival] || festival} group on Loud Neighbors!\n\nFind the perfect group schedule based on everyone's music taste.\n\n${inviteUrl}`);
                    alert('Invite link copied!');
                  }
                }} className="text-white/30 text-xs hover:text-white/50">Invite</button>
                <button onClick={() => setConfirmModal({ groupId: activeGroup.group.id, isOnly: (activeGroup.group?.members?.length || 0) <= 1, fromGroupView: true })}
                  className="text-red-400/50 text-xs hover:text-red-400">{(activeGroup.group?.members?.length || 0) <= 1 ? 'Delete' : 'Leave'}</button>
              </div>
            </div>

            {/* Members */}
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              {(activeGroup.group?.members || []).map(m => (
                <div key={m.user_id} className="flex flex-col items-center flex-shrink-0">
                  {m.profile_image_url ? (
                    <img src={m.profile_image_url} alt="" className="w-8 h-8 rounded-full border border-white/20 object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/40 text-[10px] font-bold">
                      {(m.display_name || m.username || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <p className="text-white/40 text-[9px] mt-0.5 truncate max-w-[50px]">{m.username || m.display_name}</p>
                </div>
              ))}
            </div>


            {/* Schedule content */}
            {(() => {
              const sched = activeGroup.optimal;
              if (!sched) return <p className="text-white/20 text-sm text-center py-8">No schedule computed yet</p>;
              return (
                <>
                  {['friday', 'saturday', 'sunday'].map(day => {
                    const dayActs = sched.schedule?.[day] || [];
                    if (!dayActs.length) return null;
                    const dayScore = Math.round(sched.day_scores?.[day] || 0);
                    const barColor = { friday: 'bg-gradient-to-r from-orange-500 to-pink-500', saturday: 'bg-gradient-to-r from-blue-500 to-cyan-400', sunday: 'bg-gradient-to-r from-violet-500 to-fuchsia-400' };
                    return (
                      <div key={day}>
                        <div className="flex items-center gap-3 mb-3">
                          <p className="text-white font-black text-sm uppercase w-20 flex-shrink-0">{day}</p>
                          <div className="flex-1 h-3 bg-white/10 overflow-hidden rounded-full">
                            <div className={`h-full ${barColor[day]} rounded-full`} style={{ width: `${dayScore}%` }} />
                          </div>
                          <span className="text-white font-black text-sm w-8 text-right">{dayScore}</span>
                        </div>
                        <div className="space-y-1.5">
                          {[...dayActs].sort((a, b) => new Date(b.set_start) - new Date(a.set_start)).map((a, i) => (
                            <div key={a.act_id || i} className="flex items-center gap-3 bg-white/5 rounded-xl px-3 py-2">
                              {a.artist_image_url ? (
                                <img src={a.artist_image_url} alt="" className="w-9 h-9 rounded-full border border-white/10 object-cover flex-shrink-0" loading="lazy" />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/30 font-bold text-xs flex-shrink-0">
                                  {(a.artist_name || '?')[0].toUpperCase()}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-white font-medium text-sm leading-tight truncate">{a.artist_name}</p>
                                <p className="text-white/40 text-[11px]">
                                  {a.stage} &middot; {formatTime(a.set_start)}{a.set_end ? ` – ${formatTime(a.set_end)}` : ''}
                                </p>
                              </div>
                              <ScoreBadge score={a.score} />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            })()}

          </div>
        ) : showCustomResults && customSchedule?.has_custom ? (
          /* Custom schedule results view */
          <div className="space-y-6">
            {/* Header with share + edit */}
            <div className="flex items-center gap-3">
              <h2 className="text-white font-black text-lg uppercase">{user?.username || user?.display_name || 'My'}'s Schedule</h2>
              <div className="relative w-10 h-10 flex-shrink-0">
                <ScoreRing score={customSchedule.composite_score || 0} size={40} strokeWidth={3} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white font-black text-[10px]">{customSchedule.composite_score || 0}</span>
                </div>
              </div>
              <div className="flex gap-3 ml-auto">
                <button onClick={() => {
                  // Build a schedule-shaped object from custom schedule for ShareModal
                  const cs = customSchedule;
                  const sortByTime = (acts) => [...acts].sort((a, b) => new Date(a.set_start) - new Date(b.set_start));
                  setShareScheduleData({
                    scored: true,
                    composite_score: cs.composite_score || 0,
                    day_scores: cs.day_scores || {},
                    days: Object.fromEntries(['friday', 'saturday', 'sunday'].map(d => [d, {
                      top5: (cs.custom_schedule?.[d] || []).sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 5),
                      all: cs.custom_schedule?.[d] || [],
                    }])),
                    optimal_schedule: Object.fromEntries(['friday', 'saturday', 'sunday'].map(d => [d, sortByTime(cs.custom_schedule?.[d] || [])])),
                  });
                  setShowShare(true);
                }} className="bg-white text-black font-bold text-xs px-4 py-1.5 rounded-full hover:bg-white/90">Share</button>
                <button onClick={() => {
                  const ids = Object.values(customSchedule.custom_schedule || {}).flat().map(a => a.act_id).filter(Boolean);
                  setBuilderInitialIds(ids);
                  setShowCustomResults(false);
                  setShowBuilder(true);
                }} className="text-white/30 text-xs hover:text-white/50">Edit</button>
              </div>
            </div>

            {/* Day sections */}
            {['friday', 'saturday', 'sunday'].map(day => {
              const dayActs = customSchedule.custom_schedule?.[day] || [];
              if (!dayActs.length) return null;
              const dayScore = Math.round(customSchedule.day_scores?.[day] || 0);
              const barColor = { friday: 'bg-gradient-to-r from-orange-500 to-pink-500', saturday: 'bg-gradient-to-r from-blue-500 to-cyan-400', sunday: 'bg-gradient-to-r from-violet-500 to-fuchsia-400' };
              return (
                <div key={day}>
                  <div className="flex items-center gap-3 mb-3">
                    <p className="text-white font-black text-sm uppercase w-20 flex-shrink-0">{day}</p>
                    <div className="flex-1 h-3 bg-white/10 overflow-hidden rounded-full">
                      <div className={`h-full ${barColor[day]} rounded-full`} style={{ width: `${dayScore}%` }} />
                    </div>
                    <span className="text-white font-black text-sm w-8 text-right">{dayScore}</span>
                  </div>
                  <div className="space-y-1.5">
                    {[...dayActs].sort((a, b) => new Date(b.set_start) - new Date(a.set_start)).map((a, i) => (
                      <div key={a.act_id || i} className="flex items-center gap-3 bg-white/5 rounded-xl px-3 py-2">
                        {a.artist_image_url ? (
                          <img src={a.artist_image_url} alt="" className="w-9 h-9 rounded-full border border-white/10 object-cover flex-shrink-0" loading="lazy" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/30 font-bold text-xs flex-shrink-0">
                            {(a.artist_name || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-sm leading-tight truncate">{a.artist_name}</p>
                          <p className="text-white/40 text-[11px]">
                            {a.stage} &middot; {formatTime(a.set_start)}{a.set_end ? ` – ${formatTime(a.set_end)}` : ''}
                          </p>
                        </div>
                        <ScoreBadge score={a.score} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : showBuilder ? (
          <ScheduleBuilder
            acts={(() => {
              // Merge scores from schedule into acts
              if (!schedule?.days) return acts;
              const scoreMap = {};
              for (const day of ['friday', 'saturday', 'sunday']) {
                for (const a of (schedule.days[day]?.all || schedule.days[day]?.top5 || [])) {
                  if (a.artist_spotify_id) scoreMap[a.artist_spotify_id] = a.score || 0;
                }
              }
              return acts.map(a => ({ ...a, score: scoreMap[a.artist_spotify_id] || a.score || 0 }));
            })()}
            optimalSchedule={schedule?.optimal_schedule}
            initialSelectedIds={builderInitialIds}
            startFresh={!schedule?.scored && !builderInitialIds?.length}
            onComplete={handleSaveCustom}
            onCancel={() => { setShowBuilder(false); setBuilderInitialIds(null); }}
          />
        ) : schedule?.scored && showResults ? (
          <ResultsView schedule={schedule} onReplay={() => {
            setIsReplay(true);
            setPendingScoreData(schedule);
            setShowScoringAnim(true);
            window._lnSkipToWrapped = () => {
              setShowScoringAnim(false);
              setWrappedKey(k => k + 1);
              setShowWrapped(true);
            };
          }} onRescore={handleScore} onShare={() => setShowShare(true)} onBack={() => setShowResults(false)} />
        ) : schedule?.scored ? (
          /* Festival overview — scored but not viewing results */
          <div>
            <div className="text-center py-6 mb-4">
              <h1 className="text-white font-black text-3xl md:text-4xl uppercase tracking-tight mb-3">{FESTIVAL_DISPLAY[festival] || festival}</h1>
              <button
                onClick={() => {
                  setIsReplay(true);
                  setPendingScoreData(schedule);
                  setShowScoringAnim(true);
                  window._lnSkipToWrapped = () => {
                    setShowScoringAnim(false);
                    setWrappedKey(k => k + 1);
                    setShowWrapped(true);
                  };
                }}
                className="bg-white text-black font-black uppercase tracking-wider text-sm px-8 py-3 rounded-full hover:bg-white/90 transition-all shadow-[0_0_40px_rgba(255,255,255,0.15)]"
              >
                View Your Festival Highlights
              </button>
            </div>

            {/* Your Schedules */}
            <div className="max-w-md mx-auto mb-6">
              <h3 className="text-white font-black text-sm uppercase tracking-wider mb-3">Your Schedules</h3>

              {/* Optimal Schedule */}
              <button
                onClick={() => setShowResults(true)}
                className="w-full flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 mb-2 hover:bg-white/10 transition-all text-left"
              >
                <div className="relative w-10 h-10 flex-shrink-0">
                  <ScoreRing score={schedule.composite_score} size={40} strokeWidth={3} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white font-black text-[10px]">{schedule.composite_score}</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm">Your Optimal Schedule</p>
                  <p className="text-white/40 text-xs">Based on your music taste</p>
                </div>
                <svg className="w-4 h-4 text-white/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Custom schedule (if exists) */}
              {customSchedule?.has_custom && (
                <button
                  onClick={() => setShowCustomResults(true)}
                  className="w-full flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 mb-2 hover:bg-white/10 transition-all text-left"
                >
                  {user?.profile_image_url ? (
                    <img src={user.profile_image_url} alt="" className="w-10 h-10 rounded-full border border-white/20 object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {user?.display_name?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm">{user?.username || user?.display_name || 'My'}'s Schedule</p>
                    <p className="text-white/40 text-xs">{Object.values(customSchedule.custom_schedule || {}).flat().length} acts selected</p>
                  </div>
                  <div className="relative w-8 h-8 flex-shrink-0">
                    <ScoreRing score={customSchedule.composite_score || 0} size={32} strokeWidth={2} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white font-black text-[8px]">{customSchedule.composite_score || 0}</span>
                    </div>
                  </div>
                </button>
              )}

              {/* Create custom — only show if no custom schedule exists */}
              {!customSchedule?.has_custom && (
                <button
                  onClick={() => setShowBuilder(true)}
                  className="w-full flex items-center gap-3 bg-white/5 border border-white/10 border-dashed rounded-xl px-4 py-3 hover:bg-white/10 transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm">Create Your Own Schedule</p>
                    <p className="text-white/40 text-xs">Build a custom lineup</p>
                  </div>
                  <svg className="w-4 h-4 text-white/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Your Groups */}
            <div className="max-w-md mx-auto mb-6">
              <h3 className="text-white font-black text-sm uppercase tracking-wider mb-3">Your Groups</h3>

              {groups.map(g => (
                <button
                  key={g.id}
                  onClick={() => handleViewGroup(g.id)}
                  className="w-full flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 mb-2 hover:bg-white/10 transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm">{g.name}</p>
                    <p className="text-white/40 text-xs">{g.member_count} member{g.member_count !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmModal({ groupId: g.id, isOnly: (g.member_count || 0) <= 1 });
                      }}
                      className="text-red-400/30 text-[10px] font-bold hover:text-red-400 transition-colors"
                    >
                      {(g.member_count || 0) <= 1 ? 'Delete' : 'Leave'}
                    </span>
                    <svg className="w-4 h-4 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}

              {/* Create group */}
              {showGroupCreate ? (
                <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                  <input
                    type="text"
                    value={groupName}
                    onChange={e => setGroupName(e.target.value)}
                    placeholder="Group name..."
                    className="w-full bg-transparent border-b border-white/20 text-white text-sm py-1 mb-3 focus:outline-none focus:border-white/40 placeholder-white/30"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleCreateGroup(); }}
                  />
                  <div className="flex gap-2">
                    <button onClick={handleCreateGroup} disabled={!groupName.trim()}
                      className="bg-white text-black font-bold text-xs px-4 py-1.5 rounded-full hover:bg-white/90 disabled:opacity-50">
                      Create & Share
                    </button>
                    <button onClick={() => { setShowGroupCreate(false); setGroupName(''); }}
                      className="text-white/30 text-xs hover:text-white/50">Cancel</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={groups.length < 5 ? () => setShowGroupCreate(true) : undefined}
                  disabled={groups.length >= 5}
                  className={`w-full flex items-center gap-3 border border-dashed rounded-xl px-4 py-3 text-left ${
                    groups.length < 5 ? 'bg-white/5 border-white/10 hover:bg-white/10 transition-all' : 'bg-white/[0.02] border-white/5 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm">Find Your Group's Perfect Schedule</p>
                    {groups.length >= 5 ? (
                      <p className="text-white/40 text-xs">Already in 5 groups — remove yourself from an existing group to create a new one</p>
                    ) : (
                      <p className="text-white/40 text-xs">Create a group and invite friends</p>
                    )}
                  </div>
                </button>
              )}
            </div>

          </div>
        ) : (
          <LineupView
            acts={acts}
            onScore={handleScore}
            scoring={scoring}
            hasListeningData={hasListeningData}
            onCreateCustom={() => setShowBuilder(true)}
            customSchedule={customSchedule}
            onViewCustom={customSchedule?.has_custom ? () => setShowCustomResults(true) : undefined}
            festivalLabel={FESTIVAL_DISPLAY[festival] || festival}
          />
        )}
      </div>
      {!showResults && !showBuilder && (
        <div className="flex-shrink-0 pb-16 md:pb-2 pt-1 px-4">
          <p className="text-white/15 text-[9px] text-center">
            Not affiliated with, endorsed by, or sponsored by Coachella, Goldenvoice, or AEG Presents. Coachella&reg; is a registered trademark of Goldenvoice. Artist data provided by Spotify.
          </p>
        </div>
      )}
    </div>
  );
}
